// @ts-strict-ignore
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import Tag from 'antd/lib/tag';
import { dayjs } from '@/extendedDayjs';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectAppName,
    selectAppTimeFormatSetting,
    selectDestinationInfoByDestinationStepConfigId,
    selectEffectiveTimezone,
    selectIsAutomationEnabled,
    selectIsWorkflowsCIStepEnabled,
    selectOrgUser,
    selectStepConfigDataFromStepGraphs,
    selectStepRun,
    selectUserById,
    selectWorkflowId,
} from '@/selectors/selectors';
import Icon, {
    ApiOutlined,
    BranchesOutlined,
    CalendarOutlined,
    CheckSquareOutlined,
    CarryOutOutlined,
    CheckCircleFilled,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleFilled,
    CloseCircleOutlined,
    CloseOutlined,
    CloudDownloadOutlined,
    CloudSyncOutlined,
    CloudUploadOutlined,
    CopyOutlined,
    DeleteOutlined,
    DeleteRowOutlined,
    DiffOutlined,
    DisconnectOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    FieldBinaryOutlined,
    FileDoneOutlined,
    FileSearchOutlined,
    FileTextOutlined,
    IdcardOutlined,
    InfoCircleOutlined,
    KeyOutlined,
    LoadingOutlined,
    MergeOutlined,
    MessageOutlined,
    MinusCircleFilled,
    MinusCircleOutlined,
    MinusOutlined,
    MobileOutlined,
    NodeExpandOutlined,
    NotificationOutlined,
    PaperClipOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    PlusOutlined,
    PullRequestOutlined,
    ReconciliationOutlined,
    RedoOutlined,
    ReloadOutlined,
    RiseOutlined,
    SafetyOutlined,
    SearchOutlined,
    SettingOutlined,
    SignalFilled,
    SwapOutlined,
    SyncOutlined,
    TabletOutlined,
    TagOutlined,
    TagsOutlined,
    TeamOutlined,
    ThunderboltFilled,
    ThunderboltOutlined,
    ToolOutlined,
    ToTopOutlined,
    TranslationOutlined,
    UndoOutlined,
    UploadOutlined,
    UsergroupAddOutlined,
    UserSwitchOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import {
    AppleAppStorePhasedReleaseState,
    AppleAppStoreState,
    AppPlatform,
    AppStoreReviewSubmissionItemState,
    AppStoreType,
    AutomationConditionResult,
    AutomationConditionTooltipType,
    AutomationStatus,
    AutomationTimelineEventRowKey,
    BuildStatus,
    ChecklistItemPlacement,
    CommonBuildData,
    FileChangesResult,
    GooglePlayStoreState,
    IntegrationType,
    OrgUser,
    RegressionStatus,
    ReleaseCreatedOrigin,
    ReleaseStepStatus,
    ReleaseStepType,
    RollbackResigningStrategy,
    SettingType,
    TimelineEvent,
    TimelineEventAppStoreVersionAppStoreBuildChangedData,
    TimelineEventAppStoreVersionBetaSubmittedForReviewData,
    TimelineEventAppStoreVersionPhasedReleaseUpdatedData,
    TimelineEventHealthMetricsSkippedForStaleDataData,
    TimelineEventAppStoreVersionStatusUpdatedData,
    TimelineEventAutomationConditionsData,
    TimelineEventBatchActionRequestedData,
    TimelineEventBinaryArtifactAppStoreUploadData,
    TimelineEventChecklistItemDueUpcomingData,
    TimelineEventChecklistItemStatusUpdatedData,
    TimelineEventDsymArtifactStabilityMonitoringUploadData,
    TimelineEventFeatureFlagToggledData,
    TimelineEventInitiator,
    TimelineEventIntegrationUpdatedData,
    TimelineEventMarkReleaseActiveForAppStoreData,
    TimelineEventMetadataTranslationsExportedData,
    TimelineEventMetadataTranslationsUploadedData,
    TimelineEventReleaseBranchCreatedData,
    TimelineEventReleaseKickedOffData,
    TimelineEventReleaseCodePromotedData,
    TimelineEventReleaseCreatedData,
    TimelineEventReleaseMarkedRejectedData,
    TimelineEventReleasePilotChangedData,
    TimelineEventReleaseReleasedData,
    TimelineEventReleaseStepBetaSoakFinishedData,
    TimelineEventReleaseStepBetaSoakStartedData,
    TimelineEventReleaseStepChecklistCompletedData,
    TimelineEventReleaseStepCIBuildFailureOverriddenData,
    TimelineEventReleaseStepMetadataApprovalChangedData,
    TimelineEventReleaseStepRCBuildStatusChangedData,
    TimelineEventReleaseStepConfigCreatedData,
    TimelineEventReleaseStepConfigDeletedData,
    TimelineEventReleaseStepRegressionTestingStatusChangedData,
    TimelineEventReleaseStepScreenshotsApprovalChangedData,
    TimelineEventReleaseStepStagedScreenshotsAppliedData,
    TimelineEventReleaseStepStatusChangedData,
    TimelineEventReleaseStepTranslationsApprovalChangedData,
    TimelineEventReleaseSubmissionItemAddedData,
    TimelineEventReleaseSubmissionItemRemovedData,
    TimelineEventReleaseSubmissionItemStatusUpdatedData,
    TimelineEventReleaseSubmittedData,
    TimelineEventReleaseTaggedData,
    TimelineEventReleaseTargetKickoffDateUpdatedData,
    TimelineEventReleaseTargetReleaseDateUpdatedData,
    TimelineEventReleaseTargetSubmissionDateUpdatedData,
    TimelineEventReleaseVersionBumpedData,
    TimelineEventReleaseVersionChangedData,
    TimelineEventRollbackResigningStrategyData,
    TimelineEventRolloutSupersededData,
    TimelineEventRowValueType,
    TimelineEventStatus,
    TimelineEventTicketListData,
    TimelineEventTooltipLine,
    TimelineEventTranslationsExportedData,
    TimelineEventTranslationsUpdatedData,
    TimelineEventTranslationsUploadedData,
    TimelineEventType,
    TimelineEventTriageIssueTicketCreatedData,
    TimelineUpcomingEventData,
    TimelineEventLastAutomationRunData,
    AutoSelectLatestBuildStrategy,
    TimelineEventVCSCherryPickPRCreatedData,
    TimelineEventVCSReleaseAssetsUploadedData,
} from '@/types';
import {
    antGrey,
    colorWithAlpha,
    getAvatarColorForName,
    runwayColors,
    rwColors,
    tagStyle,
} from '../styles/globalStyles';
import { formatDateWithTimezone, TimeFormat } from '@/timeHelpers';
import { AppComponentProps } from '@/typescriptHelpers';
import { warn } from '@/utils/logger';
import {
    getAutoSelectLatestBuildStrategyFieldData,
    smallAutomationIconForStatus,
    targetDateLabelIconDateString,
} from '../automations/automationHelperComponents';
import {
    automationDisplayNameForAutomationId,
    colorForAutomationStatus,
    convertAndroidStagedRolloutPercentage,
    displayCIText,
    displayNameForTransactionType,
    displayReviewItemStatus,
    displayStringForDestination,
    displayStringForRolloutPercentage,
    displayTextForAppPlatform,
    displayTextForAutomationStatus,
    displayConsoleNameForAppStoreType,
    displayTextForBuildStatus,
    displayTextForChecklistItemStatusType,
    displayTextForReleaseStepType,
    displayUserNameLong,
    displayUserNameShort,
    getTimelineColorForChecklistItemStatusType,
    getTimelineIconForChecklistItemStatusType,
    reviewItemNameForType,
} from '@/helpers/displayHelpers';
import Avatar from 'antd/lib/avatar';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { directorySyncProvider } from '@/helpers/directorySyncProviders';
import { useMdmPlatformOverride } from '@/helpers/mdmPlatformOverride';
import {
    copyToClipboard,
    displayNameForCapability,
    getIsAppleAppStore,
    getIsGooglePlayStore,
    STRING_FOR_APP_STORE_STATE,
    STRING_FOR_CHECKLIST_ITEM_PLACEMENT,
    STRING_FOR_PHASED_RELEASE_STATE,
    STRING_FOR_REGRESSION_STATUS,
    STRING_FOR_RELEASE_STEP_STATUS,
} from '@/helpers';
import { reviewSubmissionItemsSummaryText } from '@/components/AppStoreItems/AppStoreSubmissionCollapse';
import { capitalize, compact } from 'lodash';
import { ReleaseTimelineSectionContext } from './EventsTimeline';
import { renderTextWithMarkdownLinks } from '@/helpers/automationHelpers';
import Collapse from 'antd/lib/collapse';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { Flex } from 'antd/lib';
import { Destination } from '@/types/destinations';
import Button from 'antd/lib/button';
import { AppAutomationSettings } from '../automations/AppAutomationSettings';
import { batchActionSummaryInfosFromBatchTimelineEventData } from './timelineEventHelpers';
import { TimelineEventCounter } from './TimelineEventCounter';
import { ChecklistItemOwnerAttribution } from './ChecklistItemOwnerAttribution';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';
import { globalMessage } from '@/utils/globalMessage';
import { useRetryAutomationMutation } from '@/middlewares/defaultApi';
import { createAppSettingsRouteWithSettingType } from '@/helpers/routeHelpers';

const generateBuildPairString = (data: Partial<CommonBuildData>) => {
    const stringComponents = [];

    if (!data) {
        warn('data is null in generateBuildPairString');
        return null;
    }

    if (data.appStoreBuildNumber) {
        stringComponents.push(`#${data.appStoreBuildNumber}`);
    }

    if (data.ciBuildNumber) {
        if (data.ciBuildIntegrationType && displayNameForIntegrationType(data.ciBuildIntegrationType)) {
            stringComponents.push(
                `(${displayNameForIntegrationType(data.ciBuildIntegrationType)} #${data.ciBuildNumber})`,
            );
        } else {
            stringComponents.push(`(#${data.ciBuildNumber})`);
        }
    }

    if (stringComponents.length > 0) {
        return stringComponents.join(' ');
    }

    return null;
};

const generateBetaBuildInfoString = (data: Partial<CommonBuildData>) => {
    if (!data) {
        return null;
    }

    const stringComponents = [];

    if (
        data.ciBuildNumber &&
        data.ciBuildIntegrationType &&
        displayNameForIntegrationType(data.ciBuildIntegrationType)
    ) {
        stringComponents.push(`${displayNameForIntegrationType(data.ciBuildIntegrationType)} #${data.ciBuildNumber}`);
    }

    if (data.appStoreBuildNumber && data.betaServiceName) {
        stringComponents.push(`${data.betaServiceName} #${data.appStoreBuildNumber}`);
    }

    if (stringComponents.length > 0) {
        return `(${compact(stringComponents).join(', ')})`;
    }

    return null;
};

const generateBuildInfoString = (
    data: Partial<CommonBuildData>,
    pluralPrefix = '',
    pluralSuffix = '',
    singlePrefix = '',
    singleSuffix = '',
) => {
    let buildsArray =
        data?.multipleBuildData?.map((build) =>
            generateBuildPairString({
                ...data,
                ...build,
            }),
        ) ?? [];

    buildsArray = compact(buildsArray);

    if (buildsArray.length > 1) {
        const builds = `${buildsArray.slice(0, -1).join(', ')} and ${buildsArray[buildsArray.length - 1]}`;
        return compact([pluralPrefix, `${builds}`, pluralSuffix]).join(' ');
    }

    return compact([singlePrefix, generateBuildPairString(data), singleSuffix]).join(' ');
};

export const attributionIconForTargetEvent: (props: { timelineEvent: TimelineEvent }) => React.ReactNode | null = ({
    timelineEvent,
}) => {
    const { automationRun } = timelineEvent.eventData as TimelineUpcomingEventData;
    const automationEnabled = useSelector((state: RootState) =>
        selectIsAutomationEnabled(state, timelineEvent.appId, automationRun?.id),
    );
    const targetDate = timelineEvent.triggeredAt ? dayjs(timelineEvent.triggeredAt) : null;

    if (!targetDate || !automationRun) {
        return null;
    }

    const isAutomationEnabled =
        automationRun && automationEnabled && targetDate.isSame(automationRun.scheduledAt, 'day');

    if (!isAutomationEnabled) {
        return null;
    }
    const { overrideColor } = targetDateLabelIconDateString(automationRun, targetDate);
    const icon = smallAutomationIconForStatus(automationRun.status, overrideColor ?? runwayColors.primary);
    return (
        <div
            style={{
                marginTop: -1,
            }}
        >
            {icon}
        </div>
    );
};

export const attributionIconForEvent: (props: {
    timelineEvent: TimelineEvent;
    user: OrgUser;
    appPlatform: AppPlatform;
}) => React.ReactNode | null = ({ timelineEvent, user }) => {
    if (
        timelineEvent.type === TimelineEventType.OrgDirectorySyncEnabled ||
        timelineEvent.type === TimelineEventType.OrgDirectorySyncDisabled
    ) {
        const provider = directorySyncProvider(timelineEvent.additionalData?.directorySyncProvider);
        if (!provider) {
            return null;
        }
        return (
            <Tooltip title={`Via ${provider.label} directory sync`}>
                <Avatar
                    src={provider.icon}
                    alt={provider.label}
                    style={{ width: 22, height: 22, marginRight: '-2px' }}
                />
            </Tooltip>
        );
    }
    if (user || timelineEvent.userId) {
        const displayName = user ? displayUserNameShort(user) : null;
        const src = user?.commsAvatars?.image32;
        const userDisplayName = displayName ?? timelineEvent.userId;
        const backgroundColor = getAvatarColorForName(userDisplayName);

        return (
            <Tooltip title={user ? displayUserNameLong(user) : userDisplayName}>
                <Avatar
                    src={src}
                    style={{
                        width: 18,
                        height: 18,
                        lineHeight: '18px',
                        fontSize: 10,
                        flexShrink: 0,
                        backgroundColor,
                    }}
                >
                    {userDisplayName?.charAt(0).toUpperCase()}
                </Avatar>
            </Tooltip>
        );
    } else if (timelineEvent.triggeredBy === TimelineEventInitiator.Runway) {
        return (
            <Tooltip title="Automated by Runway">
                <Icon
                    component={ThunderboltFilled}
                    style={{
                        display: 'block',
                        fontSize: 18,
                        color: runwayColors.primary,
                    }}
                />
            </Tooltip>
        );
    }

    const commonBuildData = timelineEvent.eventData as CommonBuildData;

    let integrationType: IntegrationType;

    switch (timelineEvent.type) {
        case TimelineEventType.ReleaseSubmitted:
        case TimelineEventType.ReleaseReleased:
        case TimelineEventType.AppStoreVersionStatusUpdated:
        case TimelineEventType.AppStoreVersionBetaSubmittedForReview:
        case TimelineEventType.AppStoreVersionPhasedReleaseUpdated:
        case TimelineEventType.AppStoreVersionReleaseNotesUpdated:
        case TimelineEventType.AppStoreVersionAppStoreBuildChanged:
            integrationType = commonBuildData?.appStoreBuildIntegrationType;
            break;
        case TimelineEventType.RolloutSuperseded: {
            const rolloutSupersededData = timelineEvent.eventData as TimelineEventRolloutSupersededData;
            integrationType = rolloutSupersededData.appStoreIntegrationType;
            break;
        }
        default: {
            integrationType = commonBuildData?.ciBuildIntegrationType;
            break;
        }
    }

    if (integrationType) {
        var [src, alt] = iconAndAltForIntegrationType(integrationType);
        if (timelineEvent.type == TimelineEventType.AppStoreVersionPhasedReleaseUpdated) {
            alt = 'updated by an external user/trigger';
        }
        return (
            <Tooltip title={alt}>
                <Avatar
                    src={src}
                    alt={alt}
                    style={{
                        width: 22,
                        height: 22,
                        marginRight: '-2px',
                    }}
                ></Avatar>
            </Tooltip>
        );
    }

    return null;
};

export const titleForEvent: (props: {
    timelineEvent: TimelineEvent;
    appPlatform: AppPlatform;
    appStoreType: AppStoreType;
    associatedUser?: OrgUser;
    isWorkflowsCIStepEnabled: boolean;
    use24HourTimeFormat?: boolean;
    effectiveTimezone?: string;
}) => string = ({
    timelineEvent,
    appPlatform,
    appStoreType,
    associatedUser,
    isWorkflowsCIStepEnabled,
    use24HourTimeFormat = false,
    effectiveTimezone,
}) => {
    const { type, releaseData, eventData, userId, title } = timelineEvent;
    if (title?.length > 0) {
        return title;
    }

    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const isFailedEvent =
        timelineEvent.status === TimelineEventStatus.Failed ||
        timelineEvent.automationData?.automationStatus === AutomationStatus.Failed;

    let eventTitle: string = type;
    switch (type) {
        case TimelineEventType.TargetKickoffDateUpcoming: {
            eventTitle = 'Target kickoff date';
            break;
        }
        case TimelineEventType.TargetSubmissionDateUpcoming: {
            eventTitle = 'Target submission date';
            break;
        }
        case TimelineEventType.TargetReleaseDateUpcoming: {
            eventTitle = 'Target release date';
            break;
        }
        case TimelineEventType.ReleaseCreated:
        case TimelineEventType.ReleaseCreatedUpcomingRelease: {
            const data = eventData as TimelineEventReleaseCreatedData;

            eventTitle = 'Release created';
            if (releaseData?.displayableVersion) {
                eventTitle = `Release ${releaseData.displayableVersion} created`;
            }

            if (data) {
                switch (data.releaseOrigin) {
                    case ReleaseCreatedOrigin.Draft:
                        if (data.createdByUser) {
                            eventTitle += ` by ${data.createdByUser}`;
                        }
                        break;
                    case ReleaseCreatedOrigin.AutoCreatedByRunway:
                        eventTitle += ` by Runway`;
                        break;
                    case ReleaseCreatedOrigin.AppStoreScraper:
                        eventTitle += ` from app store`;
                        break;
                    case ReleaseCreatedOrigin.TaggedLatestReleaseOnStaticReleaseBranch:
                        eventTitle += ` by commit after latest tagged release`;
                        break;
                    case ReleaseCreatedOrigin.FeatureAffiliationDetection:
                        eventTitle += ` by detected feature affiliation`;
                        break;
                    case ReleaseCreatedOrigin.BranchDetection:
                        eventTitle += ` by detected branch`;
                        break;
                    default:
                        break;
                }
            }

            break;
        }
        case TimelineEventType.ReleaseVersionBumped: {
            const data = eventData as TimelineEventReleaseVersionBumpedData;

            eventTitle = 'Release version bumped';
            if (data?.bumpedVersion && data?.branch) {
                eventTitle = `Version bumped on ${data.branch} to ${data.bumpedVersion}`;
            }
            break;
        }
        case TimelineEventType.ReleaseBranchCreated: {
            const data = eventData as TimelineEventReleaseBranchCreatedData;

            if (isFailedEvent) {
                eventTitle =
                    data?.releaseBranch ?
                        `Failed to create '${data.releaseBranch}' branch`
                    :   'Failed to create release branch';
            } else {
                eventTitle = 'Release branch created';
                if (data?.releaseBranch) {
                    eventTitle = `'${data.releaseBranch}' branch created`;
                }
            }
            break;
        }
        case TimelineEventType.ReleaseKickedOff: {
            const data = eventData as TimelineEventReleaseKickedOffData;

            if (isFailedEvent) {
                eventTitle =
                    data?.releaseBranch ?
                        `Failed to kick off release on '${data.releaseBranch}'`
                    :   'Failed to kick off release';
            } else {
                eventTitle = 'Release kicked off';
                if (data?.releaseBranch) {
                    eventTitle = `Release kicked off on '${data.releaseBranch}'`;
                }
            }
            break;
        }
        case TimelineEventType.ReleaseCodePromoted: {
            const data = eventData as TimelineEventReleaseCodePromotedData;
            if (isFailedEvent) {
                eventTitle =
                    data?.releaseBranch && data?.workingBranch ?
                        `Failed to promote code from ${data.workingBranch} to ${data.releaseBranch}`
                    :   'Failed to promote code from working branch to release branch';
            } else {
                eventTitle = 'Release code promoted from working branch to release branch';
                if (data?.releaseBranch && data?.workingBranch) {
                    eventTitle = `Code promoted from ${data.workingBranch} to ${data.releaseBranch}`;
                }
            }
            break;
        }
        case TimelineEventType.ReleaseSubmitted: {
            const data = eventData as TimelineEventReleaseSubmittedData;

            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'Build submitted for review';
            } else if (data?.reviewSubmissionItems?.length) {
                const summary = reviewSubmissionItemsSummaryText(data.reviewSubmissionItems.map((item) => item.type));

                eventTitle = `Bundle (build ${generateBuildPairString(data)}, ${summary}) submitted for review`;
            } else {
                eventTitle = `${generateBuildInfoString(data, 'Builds', 'were', 'Build', '')} submitted for review`;
            }
            break;
        }
        case TimelineEventType.ReviewSubmissionItemRemoved: {
            const data = eventData as TimelineEventReleaseSubmissionItemRemovedData;
            if (data?.item) {
                eventTitle = `Submission item (${reviewItemNameForType(data.item.type).toLowerCase()} "${
                    data.item.name
                }") removed from ${data.submissionVersion} review submission`;
            } else {
                eventTitle = 'Submission item removed from review submission';
            }
            break;
        }
        case TimelineEventType.ReviewSubmissionItemAdded: {
            const data = eventData as TimelineEventReleaseSubmissionItemAddedData;
            if (data?.item) {
                eventTitle = `Submission item (${reviewItemNameForType(data.item.type).toLowerCase()} "${
                    data.item.name
                }") added to ${data.submissionVersion} review submission`;
            } else {
                eventTitle = 'Submission item added to review submission';
            }
            break;
        }
        case TimelineEventType.ReviewSubmissionItemStatusUpdated: {
            const data = eventData as TimelineEventReleaseSubmissionItemStatusUpdatedData;

            if (!data || !data?.item) {
                warn(`TimelineEvent ${type} missing eventData or item`);
                eventTitle = 'Submission item status changed';
            } else {
                eventTitle = `Submission item (${reviewItemNameForType(data.item.type).toLowerCase()} "${
                    data.item.name
                }") status changed to "${displayReviewItemStatus(data.item.state)}"`;
            }
            break;
        }
        case TimelineEventType.ReleaseReleased: {
            const data = eventData as TimelineEventReleaseReleasedData;

            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'Build was released';
            } else if (data.expo) {
                eventTitle = `Expo update group ${data.expo.sourceGroupId} published to production channel ${data.expo.productionChannel}`;
                if (data.expo.initialRolloutPercentage != null) {
                    eventTitle += ` with an initial ${data.expo.initialRolloutPercentage}% rollout`;
                }
            } else {
                if (data.reviewSubmissionItems?.length) {
                    const summary = reviewSubmissionItemsSummaryText(
                        data.reviewSubmissionItems.map((item) => item.type),
                    );

                    eventTitle = `Bundle (build ${generateBuildPairString(data)}, ${summary}) released`;
                } else {
                    eventTitle = `${generateBuildInfoString(data, 'Builds', 'were', 'Build', 'was')} released`;
                }

                if (data.isPhasedRelease) {
                    eventTitle += '. (Phased)';
                }
            }
            break;
        }
        case TimelineEventType.ReleaseTagged: {
            const data = eventData as TimelineEventReleaseTaggedData;
            eventTitle = `Release tagged`;
            if (releaseData?.displayableVersion && data?.vcsIntegration) {
                eventTitle = `Release ${releaseData.displayableVersion} tagged in ${displayNameForIntegrationType(
                    data.vcsIntegration,
                )}`;
            }
            break;
        }
        case TimelineEventType.ReleaseStepsReadyToSubmit: {
            eventTitle = 'All steps required to submit are complete';
            break;
        }
        case TimelineEventType.ReleaseStepsReadyToRelease: {
            eventTitle = 'All steps required to release are complete';
            break;
        }
        case TimelineEventType.ReleaseTargetKickoffDateUpdated: {
            const data = eventData as TimelineEventReleaseTargetKickoffDateUpdatedData;
            eventTitle = 'Target kickoff date updated';
            if (data?.targetKickoffDate) {
                eventTitle = `Target kickoff date changed to ${formatDateWithTimezone(data.targetKickoffDate, 'fullYearAtTime', effectiveTimezone, use24HourTimeFormat)}`;
            }
            break;
        }
        case TimelineEventType.ReleaseTargetSubmissionDateUpdated: {
            const data = eventData as TimelineEventReleaseTargetSubmissionDateUpdatedData;

            eventTitle = 'Target submission date updated';
            if (data?.targetSubmissionDate) {
                eventTitle = `Target submission date changed to ${formatDateWithTimezone(data.targetSubmissionDate, 'fullYearAtTime', effectiveTimezone, use24HourTimeFormat)}`;
            }
            break;
        }
        case TimelineEventType.ReleaseTargetReleaseDateUpdated: {
            const data = eventData as TimelineEventReleaseTargetReleaseDateUpdatedData;
            eventTitle = 'Target release date updated';
            if (data?.targetReleaseDate) {
                eventTitle = `Target release date changed to ${formatDateWithTimezone(data.targetReleaseDate, 'fullYearAtTime', effectiveTimezone, use24HourTimeFormat)}`;
            }
            break;
        }
        case TimelineEventType.ReleaseVersionUpdated: {
            const data = eventData as TimelineEventReleaseVersionChangedData;
            eventTitle = `Release version updated`;
            if (data && data.newVersion && data.oldVersion) {
                eventTitle = `Release version changed from ${data.oldVersion} to ${data.newVersion}`;
            }
            break;
        }
        case TimelineEventType.ReleaseMarkedActive: {
            const data = eventData as TimelineEventMarkReleaseActiveForAppStoreData;
            const portalName = data?.storePortalName ?? 'app store';
            const displayableVersion =
                data?.displayableVersion ?? releaseData?.displayableVersion ?? releaseData?.version;
            eventTitle =
                displayableVersion ?
                    `Release ${displayableVersion} marked active to sync with ${portalName}`
                :   `Release marked active to sync with ${portalName}`;
            break;
        }
        case TimelineEventType.ReleasePilotChanged: {
            const data = eventData as TimelineEventReleasePilotChangedData;
            const pilotUserDisplayName = associatedUser ? displayUserNameShort(associatedUser) : data.releasePilotId;
            eventTitle = `Release pilot changed`;
            if (pilotUserDisplayName) {
                eventTitle = `${pilotUserDisplayName} was assigned as release pilot`;
            }
            break;
        }
        case TimelineEventType.ReleaseStepRCBuildStatusChanged: {
            const data = eventData as TimelineEventReleaseStepRCBuildStatusChangedData;
            eventTitle = `${displayCIText(isWorkflowsCIStepEnabled, 'capitalize')} build status changed`;
            if (data?.buildStatus) {
                eventTitle = `${displayCIText(isWorkflowsCIStepEnabled, 'capitalize')} build ${displayTextForBuildStatus(
                    data.buildStatus,
                ).toLowerCase()} ${generateBuildInfoString({
                    ciBuildNumber: data?.ciBuildNumber,
                    ciBuildIntegrationType: data?.ciBuildIntegrationType,
                })}`;
            }
            break;
        }
        case TimelineEventType.ReleaseStepCIBuildFailureOverridden: {
            const data = eventData as TimelineEventReleaseStepCIBuildFailureOverriddenData;
            eventTitle = data?.overridden ? 'CI build failure overridden to pass' : 'CI build failure override cleared';
            break;
        }
        case TimelineEventType.ReleaseStepConfigCreated: {
            const data = eventData as TimelineEventReleaseStepConfigCreatedData;

            eventTitle = 'Step added';
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
            } else {
                eventTitle = `"${data.stepName}" step added`;
            }

            break;
        }
        case TimelineEventType.ReleaseStepConfigDeleted: {
            const data = eventData as TimelineEventReleaseStepConfigDeletedData;

            eventTitle = 'Step deleted';
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
            } else {
                eventTitle = `"${data.stepName}" step deleted`;
            }

            break;
        }
        case TimelineEventType.ReleaseStepRegressionTestingStatusChanged: {
            const data = eventData as TimelineEventReleaseStepRegressionTestingStatusChangedData;

            eventTitle = `Regression testing status changed`;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
            } else if (data.regressionTestingStatus) {
                eventTitle = `Regression testing ${STRING_FOR_REGRESSION_STATUS[data.regressionTestingStatus]}`;
                const buildData = generateBetaBuildInfoString(data);
                if (buildData) {
                    eventTitle += ` ${buildData}`;
                }
            }

            break;
        }
        case TimelineEventType.ReleaseStepBetaSoakStarted: {
            const data = eventData as TimelineEventReleaseStepBetaSoakStartedData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'Beta soak started';
            } else {
                eventTitle = `Beta soak started ${generateBetaBuildInfoString(data) ?? ''}`;
            }
            break;
        }
        case TimelineEventType.ReleaseStepBetaSoakFinished: {
            const data = eventData as TimelineEventReleaseStepBetaSoakFinishedData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = `Beta soak ${userId ? 'ended' : 'completed'}`;
            } else {
                eventTitle = `Beta soak ${userId ? 'ended' : 'completed'} ${generateBetaBuildInfoString(data) ?? ''}`;
            }
            break;
        }
        case TimelineEventType.ReleaseStepMetadataApprovalChanged: {
            const data = eventData as TimelineEventReleaseStepMetadataApprovalChangedData;
            eventTitle = `Metadata approval status changed`;
            if (typeof data?.approved === 'boolean') {
                eventTitle = data?.approved ? `Metadata approved` : `Metadata approval rescinded`;
            }
            break;
        }
        case TimelineEventType.ReleaseStepTranslationsApprovalChanged: {
            const data = eventData as TimelineEventReleaseStepTranslationsApprovalChangedData;
            if (typeof data?.approved === 'boolean') {
                eventTitle = data?.approved ? `Translations approved` : `Translations approval rescinded`;
            } else {
                eventTitle = `Translations approval status changed`;
            }
            break;
        }
        case TimelineEventType.SyncedVCSMetadata: {
            eventTitle = 'Release metadata synced from repository';
            break;
        }
        case TimelineEventType.ReleaseStepScreenshotsApprovalChanged: {
            const data = eventData as TimelineEventReleaseStepScreenshotsApprovalChangedData;
            eventTitle = `Screenshots approval status changed`;
            if (typeof data?.approved === 'boolean') {
                eventTitle = data?.approved ? `Screenshots approved` : `Screenshots approval rescinded`;
            }
            break;
        }
        case TimelineEventType.ReleaseStepScreenshotAdded:
            eventTitle = `Screenshot added`;
            break;
        case TimelineEventType.ReleaseStepStagedScreenshotAdded:
            eventTitle = `Screenshot staged in Runway`;
            break;
        case TimelineEventType.ReleaseStepScreenshotDeleted:
            eventTitle = `Screenshot deleted`;
            break;
        case TimelineEventType.ReleaseStepScreenshotsReordered:
            eventTitle = `Screenshots reordered`;
            break;
        case TimelineEventType.ReleaseStepStagedScreenshotsApplied: {
            const data = eventData as TimelineEventReleaseStepStagedScreenshotsAppliedData;
            const changes = [
                data?.appliedCount ? `${data.appliedCount} screenshot(s) uploaded` : null,
                data?.deletedCount ? `${data.deletedCount} screenshot(s) removed` : null,
            ].filter(Boolean);
            eventTitle =
                data?.failedCount ?
                    `${data.failedCount} staged screenshot change(s) failed in App Store Connect`
                :   `${changes.length ? changes.join(' and ') : '0 screenshot(s) uploaded'} in App Store Connect`;
            break;
        }
        case TimelineEventType.RunwayEventReleaseStepAppPreviewAdded:
            eventTitle = `App preview added`;
            break;
        case TimelineEventType.RunwayEventReleaseStepAppPreviewDeleted:
            eventTitle = `App preview deleted`;
            break;
        case TimelineEventType.ReleaseStepStatusChanged: {
            const data = eventData as TimelineEventReleaseStepStatusChangedData;
            eventTitle = `Release step status changed`;
            if (data?.releaseStepType) {
                const releaseStepType = displayTextForReleaseStepType(
                    data.releaseStepType,
                    appPlatform,
                    appStoreType,
                    isWorkflowsCIStepEnabled,
                );
                eventTitle = `${releaseStepType} step status changed`;

                const releaseStepStatus = STRING_FOR_RELEASE_STEP_STATUS[data.releaseStepStatus];
                if (releaseStepStatus) {
                    eventTitle += ` to "${releaseStepStatus}"`;
                }
            }
            break;
        }
        case TimelineEventType.ReleaseStepChecklistCompleted: {
            const data = eventData as TimelineEventReleaseStepChecklistCompletedData;
            eventTitle = `Release step checklist completed`;
            if (data?.releaseStepType) {
                const releaseStepType = displayTextForReleaseStepType(
                    data.releaseStepType,
                    appPlatform,
                    appStoreType,
                    isWorkflowsCIStepEnabled,
                );
                eventTitle = `"${releaseStepType}" checklist complete`;
            }
            break;
        }
        case TimelineEventType.AppStoreVersionStatusUpdated: {
            const data = eventData as TimelineEventAppStoreVersionStatusUpdatedData;
            eventTitle = `App store version status updated`;
            if (data?.appStoreStatus) {
                let appStoreStatus = data.appStoreStatus;
                if (appStoreType === AppStoreType.Huawei) {
                    // HuaweiAppStoreState should be an int, but the timeline event json sends a string
                    if (typeof appStoreStatus === 'string') {
                        appStoreStatus = parseInt(appStoreStatus, 10);
                    }
                }
                eventTitle = `${generateBuildInfoString(data, 'Builds', '', 'Build', '')} status changed to "${
                    STRING_FOR_APP_STORE_STATE[data.appStoreStatus]
                }"`;
            }
            break;
        }
        case TimelineEventType.AppStoreVersionBetaSubmittedForReview: {
            const data = eventData as TimelineEventAppStoreVersionBetaSubmittedForReviewData;
            const buildInfo = generateBetaBuildInfoString(data);
            if (buildInfo) {
                eventTitle = `Build ${buildInfo} submitted for beta review`;
            } else {
                eventTitle = `Build submitted for beta review`;
            }

            break;
        }
        case TimelineEventType.HealthMetricsSkippedForStaleData: {
            const data = eventData as TimelineEventHealthMetricsSkippedForStaleDataData;
            const skippedMetricNames = (data?.skippedMetrics ?? []).map((metric) => metric.metricName);

            if (skippedMetricNames.length === 1) {
                eventTitle = `Health metric ${skippedMetricNames[0]} ignored because its data was out of date`;
            } else if (skippedMetricNames.length > 1) {
                eventTitle = `${skippedMetricNames.length} health metrics ignored because their data was out of date: ${skippedMetricNames.join(', ')}`;
            } else {
                eventTitle = 'Health metrics ignored because their data was out of date';
            }
            break;
        }
        case TimelineEventType.AppStoreVersionPhasedReleaseUpdated:
            {
                const data = eventData as TimelineEventAppStoreVersionPhasedReleaseUpdatedData;

                eventTitle = 'Phased release status changed';

                const isExternallyDetectedHalt =
                    timelineEvent.triggeredBy === TimelineEventInitiator.Unknown &&
                    (data?.phaseState === AppleAppStorePhasedReleaseState.Paused ||
                        data?.phaseState === GooglePlayStoreState.Halted);

                if (!data) {
                    warn(`TimelineEvent ${type} missing eventData`);
                } else if (isExternallyDetectedHalt && releaseData?.displayableVersion) {
                    eventTitle = `Runway detected version ${
                        releaseData.displayableVersion
                    } was halted in ${displayConsoleNameForAppStoreType(appStoreType)}`;
                } else if (data.expo?.targetPercentage != null) {
                    eventTitle = `Expo rollout on the Production channel updated to ${data.expo.targetPercentage}%`;
                } else if (data.phaseState) {
                    let prefix = 'Phased release';
                    if (isGooglePlayStore) {
                        prefix = `Staged rollout`;
                    }
                    eventTitle = `${prefix} ${STRING_FOR_PHASED_RELEASE_STATE[data.phaseState]}`;

                    if (data.phaseState === GooglePlayStoreState.InProgress && data.userFraction) {
                        const userFractionText = displayStringForRolloutPercentage(
                            convertAndroidStagedRolloutPercentage(data.userFraction),
                        );
                        eventTitle = `${prefix} updated to ${userFractionText}`;
                    } else if (data.dayNumber) {
                        eventTitle = `${prefix} updated to day ${data.dayNumber}`;
                    }
                } else if (data.dayNumber > 0 && data.dayNumber <= 7) {
                    // ios-only if day number is present and greater than 0 and less than max
                    eventTitle = `Phased release updated to day ${data.dayNumber}`;
                } else {
                    if (isGooglePlayStore) {
                        eventTitle = `${generateBuildInfoString(
                            data,
                            'Builds',
                            'were',
                            'Build',
                            'was',
                        )} released to all users`;
                    } else {
                        eventTitle = `${generateBuildInfoString(
                            data,
                            'Builds',
                            'have been',
                            'Build',
                            'has been',
                        )} rolled out to all users`;
                    }
                }
            }
            break;

        case TimelineEventType.AppStoreVersionReleaseNotesUpdated: {
            eventTitle = `"Release notes" updated`;
            if (isAppleAppStore) {
                eventTitle = `"What's new" notes updated`;
            }
            break;
        }
        case TimelineEventType.AppStoreVersionAppStoreBuildChanged: {
            if (timelineEvent.automationData?.automationStatus === AutomationStatus.Pending) {
                eventTitle = automationDisplayNameForAutomationId(
                    timelineEvent.automationData.automationType,
                    appStoreType,
                );
            } else {
                const data = eventData as TimelineEventAppStoreVersionAppStoreBuildChangedData;
                var buildData = undefined;

                if (!data) {
                    warn(`TimelineEvent ${type} missing eventData`);
                    if (
                        timelineEvent.status === TimelineEventStatus.Failed ||
                        timelineEvent.automationData?.automationStatus === AutomationStatus.Failed
                    ) {
                        eventTitle = `Failed trying to change selected build(s)`;
                    } else {
                        eventTitle = `Selected build changed`;
                    }
                } else {
                    if (
                        timelineEvent.status === TimelineEventStatus.Failed ||
                        timelineEvent.automationData?.automationStatus === AutomationStatus.Failed
                    ) {
                        eventTitle = `Failed trying to change selected build(s)`;

                        buildData = generateBuildInfoString(
                            data,
                            'Failed trying to change selected build(s)',
                            '',
                            'Failed trying to change selected build(s)',
                            '',
                        );
                    } else {
                        eventTitle = `Selected build changed`;

                        buildData = generateBuildInfoString(
                            data,
                            'Selected builds changed to',
                            '',
                            'Selected build changed to',
                            '',
                        );
                    }

                    if (buildData) {
                        eventTitle = buildData;
                    }
                }
            }
            break;
        }
        case TimelineEventType.ChecklistItemDueUpcoming: {
            const data = eventData as TimelineEventChecklistItemDueUpcomingData;
            eventTitle = 'Checklist item due';
            if (data?.checklistItemTitle) {
                const checklistTitle = `"${capitalize(data.checklistItemTitle)}"`;
                eventTitle =
                    data.stepName ?
                        `${checklistTitle} (${data.stepName}) checklist item`
                    :   `${checklistTitle} checklist item`;
            }
            break;
        }
        case TimelineEventType.ChecklistItemStatusUpdated: {
            const data = eventData as TimelineEventChecklistItemStatusUpdatedData;
            eventTitle = `Checklist item status updated`;
            if (data?.checklistItemTitle) {
                const checklistTitle = capitalize(data.checklistItemTitle);

                const titleString = `"${checklistTitle}"`;
                const stringComponents = [titleString];

                let statusString = 'updated';
                if (
                    data.checklistItemPlacement &&
                    data.checklistItemPlacement !== ChecklistItemPlacement.StepChecklist
                ) {
                    const checklistItemPlacementString =
                        STRING_FOR_CHECKLIST_ITEM_PLACEMENT[data?.checklistItemPlacement];
                    const checklistComponent = `Status of ${checklistItemPlacementString} item`;

                    // add prefix
                    stringComponents.unshift(checklistComponent);

                    // add status if exists
                    const checklistStatus = displayTextForChecklistItemStatusType(data?.checklistItemStatus);
                    if (checklistStatus) {
                        statusString = `changed to "${checklistStatus}"`;
                    }
                } else {
                    const releaseStepTypeString = displayTextForReleaseStepType(
                        data?.releaseStepType,
                        appPlatform,
                        appStoreType,
                        isWorkflowsCIStepEnabled,
                    );
                    if (releaseStepTypeString) {
                        stringComponents.push(`(${releaseStepTypeString})`);
                    }

                    // add status if exists
                    const checklistStatus = displayTextForChecklistItemStatusType(data?.checklistItemStatus);
                    if (checklistStatus) {
                        statusString = `changed to "${checklistStatus}"`;
                    }
                }

                // add status
                stringComponents.push(statusString);

                eventTitle = stringComponents.join(' ');
            }
            break;
        }

        case TimelineEventType.ChecklistItemCommentUpdated: {
            const data = eventData as TimelineEventChecklistItemStatusUpdatedData;
            eventTitle = `Checklist item comment updated`;
            if (data?.checklistItemTitle) {
                const checklistTitle = capitalize(data.checklistItemTitle);

                const titleString = `Comment on`;
                const stringComponents = [titleString];

                if (
                    data.checklistItemPlacement &&
                    data.checklistItemPlacement !== ChecklistItemPlacement.StepChecklist
                ) {
                    const checklistItemPlacementString =
                        STRING_FOR_CHECKLIST_ITEM_PLACEMENT[data?.checklistItemPlacement];

                    if (checklistItemPlacementString) {
                        stringComponents.push(`${checklistItemPlacementString} item "${checklistTitle}"`);
                    }
                } else {
                    stringComponents.push(`"${checklistTitle}"`);

                    const releaseStepTypeString = displayTextForReleaseStepType(
                        data?.releaseStepType,
                        appPlatform,
                        appStoreType,
                        isWorkflowsCIStepEnabled,
                    );
                    if (releaseStepTypeString) {
                        stringComponents.push(`(${releaseStepTypeString})`);
                    }
                }
                stringComponents.push(`updated`);
                eventTitle = stringComponents.join(' ');
            }
            break;
        }
        case TimelineEventType.ReleaseSchedulePaused: {
            eventTitle = `Release schedule paused`;
            break;
        }
        case TimelineEventType.ReleaseScheduleResumed: {
            eventTitle = `Release schedule resumed`;
            break;
        }
        case TimelineEventType.FeatureFlagToggled: {
            const data = eventData as TimelineEventFeatureFlagToggledData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'Feature flag toggled';
            } else {
                eventTitle = `Feature flag "${data.featureFlagName}" toggled ${data.enabled ? 'ON' : 'OFF'}`;
            }
            break;
        }
        case TimelineEventType.ReleaseMarkedRejected: {
            const data = eventData as TimelineEventReleaseMarkedRejectedData;
            eventTitle = `Release marked as rejected by ${userId}`;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
            } else if (data?.platform) {
                eventTitle = `Release marked as rejected on ${displayTextForAppPlatform(data.platform, data.appStoreType)} by ${userId}`;
            }
            break;
        }
        case TimelineEventType.AndroidRolloutStartedManually: {
            eventTitle = `Rollout started manually by ${userId}`;
            break;
        }
        case TimelineEventType.AndroidRolloutStartedFromForwardedEmail: {
            eventTitle = `Rollout started automatically`;
            break;
        }
        case TimelineEventType.BinaryArtifactAppStoreUploadSuccess: {
            const data = eventData as TimelineEventBinaryArtifactAppStoreUploadData;
            const uploadFailed =
                timelineEvent.status === TimelineEventStatus.Failed ||
                timelineEvent.automationData?.automationStatus === AutomationStatus.Failed;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = uploadFailed ? 'Artifact failed to upload to app store' : 'Artifact uploaded to app store';
            } else {
                const destination = data.destination || 'app store';
                eventTitle =
                    uploadFailed ?
                        `Artifact ${data.artifactName} failed to upload to ${destination}`
                    :   `Artifact ${data.artifactName} uploaded to ${destination}`;
            }
            break;
        }
        case TimelineEventType.BinaryArtifactAppStoreUploadFailed: {
            const data = eventData as TimelineEventBinaryArtifactAppStoreUploadData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'Artifact failed to upload to app store';
            } else {
                eventTitle = `Artifact ${data.artifactName} failed to upload to ${data.destination}`;
            }
            break;
        }
        case TimelineEventType.DsymArtifactStabilityMonitoringUploadSuccess: {
            const data = eventData as TimelineEventDsymArtifactStabilityMonitoringUploadData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'dSYMs uploaded to stability monitoring service';
            } else {
                eventTitle = `dSYMs for ${data.buildSystemName} build #${data.buildNumber} uploaded to ${data.destination}`;
            }
            break;
        }
        case TimelineEventType.VCSReleaseAssetsUploaded: {
            const data = eventData as TimelineEventVCSReleaseAssetsUploadedData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'Release assets uploaded';
            } else {
                eventTitle = `Release assets uploaded to ${data.destination}`;
            }
            break;
        }
        case TimelineEventType.DsymArtifactStabilityMonitoringUploadFailed: {
            const data = eventData as TimelineEventDsymArtifactStabilityMonitoringUploadData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'dSYMs failed to upload to stability monitoring service';
            } else {
                eventTitle = `dSYMs for ${data.buildSystemName} build #${data.buildNumber} failed to upload to ${data.destination}`;
            }
            break;
        }
        case TimelineEventType.ResignSequenceStarted: {
            const data = eventData as TimelineEventRollbackResigningStrategyData;
            eventTitle =
                data?.strategy === RollbackResigningStrategy.CIResign ?
                    'Rollback rebuild sequence started'
                :   'Re-sign sequence started';
            if (data?.stepDisplayName) {
                eventTitle = `${eventTitle} for ${data.stepDisplayName} step`;
            }
            break;
        }
        case TimelineEventType.ResignSequenceCompleted: {
            const data = eventData as TimelineEventRollbackResigningStrategyData;
            eventTitle =
                data?.strategy === RollbackResigningStrategy.CIResign ?
                    'Rollback rebuild sequence completed'
                :   'Re-sign sequence completed';
            if (data?.stepDisplayName) {
                eventTitle = `${eventTitle} for ${data.stepDisplayName} step`;
            }
            break;
        }
        case TimelineEventType.BuildResignStarted: {
            const data = eventData as TimelineEventRollbackResigningStrategyData;
            eventTitle =
                data?.strategy === RollbackResigningStrategy.CIResign ?
                    'Rollback rebuild started'
                :   'Build re-signing started';
            if (data?.stepDisplayName) {
                eventTitle = `${eventTitle} for ${data.stepDisplayName} step`;
            }
            break;
        }
        case TimelineEventType.BuildResignCompleted: {
            const data = eventData as TimelineEventRollbackResigningStrategyData;
            eventTitle =
                data?.strategy === RollbackResigningStrategy.CIResign ?
                    'Rollback rebuild completed'
                :   'Build re-signing completed';
            if (data?.stepDisplayName) {
                eventTitle = `${eventTitle} for ${data.stepDisplayName} step`;
            }
            break;
        }
        case TimelineEventType.TranslationsUploaded: {
            const data = eventData as TimelineEventTranslationsUploadedData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'Source files uploaded for translation';
            } else {
                eventTitle = `Source files uploaded from ${data.branch} for translation`;
            }
            break;
        }
        case TimelineEventType.TranslationsExported: {
            const data = eventData as TimelineEventTranslationsExportedData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'Translations exported';
            } else {
                const integrationDisplayName = displayNameForIntegrationType(data.translationsIntegrationId);
                eventTitle = `Translations exported from ${integrationDisplayName}`;
                if (data.prNumber) {
                    eventTitle += ` (PR #${data.prNumber} opened)`;
                }
            }
            break;
        }
        case TimelineEventType.TranslationsUpdated: {
            const data = eventData as TimelineEventTranslationsUpdatedData;
            const baseTitle = 'Translations updated on release branch';
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = baseTitle;
            } else {
                switch (data.changeResult) {
                    case FileChangesResult.ChangeError:
                        // shouldn't reach this point if the change was an error
                        eventTitle = 'Translations update failed';
                        break;
                    case FileChangesResult.ChangedAndDirectlyPushedAndCommitted:
                        eventTitle = baseTitle;
                        break;
                    case FileChangesResult.ChangedAndDirectlyPushedToBranchWaitingForChecksToPass:
                        eventTitle = `${baseTitle} (waiting for checks to pass)`;
                        break;
                    case FileChangesResult.ChangedAndMerged:
                        eventTitle = baseTitle;
                        break;
                    case FileChangesResult.ChangedWithPendingPR:
                        eventTitle = baseTitle;
                        if (data.prNumber) {
                            eventTitle += ` (PR #${data.prNumber} opened)`;
                        }
                        break;
                }
            }
            break;
        }
        case TimelineEventType.MetadataTranslationsExported: {
            const data = eventData as TimelineEventMetadataTranslationsExportedData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'Translations exported';
            } else {
                const integrationDisplayName = displayNameForIntegrationType(data.appStoreIntegrationId);
                eventTitle = `Translations exported to ${integrationDisplayName}`;
            }
            break;
        }
        case TimelineEventType.MetadataTranslationsUploaded: {
            const data = eventData as TimelineEventMetadataTranslationsUploadedData;
            if (!data) {
                warn(`TimelineEvent ${type} missing eventData`);
                eventTitle = 'Metadata uploaded for translation';
            } else {
                const integrationDisplayName = displayNameForIntegrationType(data.translationsIntegrationId);
                eventTitle = `Metadata uploaded to ${integrationDisplayName} for translation`;
            }
            break;
        }
        case TimelineEventType.IntegrationUpdated: {
            const data = eventData as TimelineEventIntegrationUpdatedData;
            if (data?.title) {
                eventTitle = `${data.title} integration updated`;
            } else {
                eventTitle = 'Integration updated';
            }
            break;
        }
        case TimelineEventType.ReleaseSkipped: {
            const displayableVersion = releaseData?.displayableVersion ?? releaseData?.version;
            eventTitle = displayableVersion ? `Release ${displayableVersion} skipped` : 'Release skipped';

            const additionalData = timelineEvent.additionalData;
            if (additionalData?.devRejectAttempted) {
                if (additionalData.devRejectSuccess) {
                    eventTitle += ' and app submission was successfully pulled';
                } else {
                    eventTitle += ' and app submission was not successfully pulled';
                    if (additionalData.devRejectError) {
                        eventTitle += `. Error: ${additionalData.devRejectError}`;
                    }
                }
            }
            break;
        }
        case TimelineEventType.VCSCherryPickPRCreated: {
            const data = eventData as TimelineEventVCSCherryPickPRCreatedData;
            eventTitle = `Cherry-pick PR #${data.prNumber} created`;
            break;
        }
        case TimelineEventType.VCSCherryPickError: {
            eventTitle = 'Failed to cherry-pick commit';
            break;
        }
        case TimelineEventType.VCSCherryPickConflict: {
            eventTitle = 'Failed to cherry-pick commit due to conflict';
            break;
        }
        case TimelineEventType.ReleaseStepDestinationSkipped: {
            eventTitle = 'Release destination skipped';
            break;
        }
        case TimelineEventType.ReleaseStepDestinationAdded: {
            eventTitle = 'Release destination added';
            break;
        }
    }

    return eventTitle;
};

const getTimelineColorForEvent: (props: { timelineEvent: TimelineEvent; isRetrying?: boolean }) => string = ({
    timelineEvent,
    isRetrying,
}) => {
    if (isRetrying) {
        return runwayColors.statusYellow;
    }

    let color = runwayColors.statusGreen;

    if (timelineEvent.automationData) {
        switch (timelineEvent.automationData.automationStatus) {
            case AutomationStatus.Active:
            case AutomationStatus.Success:
                return runwayColors.statusGreen;
            case AutomationStatus.Pending:
            case AutomationStatus.Skipped:
            case AutomationStatus.Paused:
            case AutomationStatus.Retrying:
            case AutomationStatus.InProgress:
                return runwayColors.statusYellow;
            case AutomationStatus.Failed:
            case AutomationStatus.Incomplete:
            case AutomationStatus.Blocked:
                return runwayColors.statusRed;
        }
    }

    switch (timelineEvent.type) {
        case TimelineEventType.ReviewSubmissionItemAdded:
        case TimelineEventType.ReviewSubmissionItemRemoved:
        case TimelineEventType.ReleaseCodePromoted:
        case TimelineEventType.ReleaseBranchCreated:
        case TimelineEventType.ReleaseKickedOff:
        case TimelineEventType.ReleaseTargetKickoffDateUpdated:
        case TimelineEventType.ReleaseTargetSubmissionDateUpdated:
        case TimelineEventType.ReleaseTargetReleaseDateUpdated:
        case TimelineEventType.AppStoreVersionAppStoreBuildChanged:
        case TimelineEventType.ChecklistItemCommentUpdated:
        case TimelineEventType.ReleaseTagged:
        case TimelineEventType.ReleasePilotChanged:
        case TimelineEventType.ReleaseVersionUpdated:
        case TimelineEventType.ReleaseMarkedActive:
        case TimelineEventType.ReleaseVersionBumped:
        case TimelineEventType.ReleaseStepBetaSoakFinished:
        case TimelineEventType.ReleaseCreated:
        case TimelineEventType.ReleaseCreatedUpcomingRelease:
        case TimelineEventType.ReleaseReleased:
        case TimelineEventType.ReleaseSubmitted:
        case TimelineEventType.ReleaseStepsReadyToSubmit:
        case TimelineEventType.ReleaseStepsReadyToRelease:
        case TimelineEventType.ReleaseStepMetadataApprovalChanged:
        case TimelineEventType.ReleaseStepTranslationsApprovalChanged:
        case TimelineEventType.ReleaseStepScreenshotsApprovalChanged:
        case TimelineEventType.ReleaseStepChecklistCompleted:
        case TimelineEventType.AndroidRolloutStartedManually:
        case TimelineEventType.AndroidRolloutStartedFromForwardedEmail:
        case TimelineEventType.SyncedVCSMetadata:
        case TimelineEventType.ReleaseStepScreenshotAdded:
        case TimelineEventType.ReleaseStepScreenshotsReordered:
        case TimelineEventType.ReleaseStepStagedScreenshotAdded:
        case TimelineEventType.ReleaseScheduleResumed:
        case TimelineEventType.BuildResignCompleted:
        case TimelineEventType.ResignSequenceStarted:
        case TimelineEventType.ResignSequenceCompleted:
        case TimelineEventType.TranslationsUploaded:
        case TimelineEventType.TranslationsExported:
        case TimelineEventType.TranslationsUpdated:
        case TimelineEventType.MetadataTranslationsExported:
        case TimelineEventType.MetadataTranslationsUploaded:
        case TimelineEventType.ReleaseStepIntegrationSettingsUpdated:
        case TimelineEventType.IntegrationUpdated:
        case TimelineEventType.VCSCherryPickPRCreated:
            return runwayColors.statusGreen;
        case TimelineEventType.JiraIssueTrackingIsMissing:
        case TimelineEventType.VCSCherryPickConflict:
        case TimelineEventType.VCSCherryPickError:
            return runwayColors.statusRed;
        case TimelineEventType.TargetReleaseDateUpcoming:
        case TimelineEventType.TargetSubmissionDateUpcoming:
        case TimelineEventType.TargetKickoffDateUpcoming:
        case TimelineEventType.AutoCreateRunwayReleasesUpcoming:
        case TimelineEventType.BinaryArtifactAppStoreUploadSuccess:
        case TimelineEventType.DsymArtifactStabilityMonitoringUploadSuccess:
        case TimelineEventType.VCSReleaseAssetsUploaded:
            return runwayColors.primary;
        case TimelineEventType.AppStoreVersionBetaSubmittedForReview:
        case TimelineEventType.ReleaseStepBetaSoakStarted:
        case TimelineEventType.AppStoreVersionReleaseNotesUpdated:
        case TimelineEventType.ReleaseSchedulePaused:
        case TimelineEventType.BuildResignStarted:
        case TimelineEventType.ReleaseUncompleted:
        case TimelineEventType.ReleaseUnskipped:
            return runwayColors.statusYellow;
        case TimelineEventType.ReleaseStepScreenshotDeleted:
        case TimelineEventType.ReleaseMarkedRejected:
        case TimelineEventType.BinaryArtifactAppStoreUploadFailed:
        case TimelineEventType.DsymArtifactStabilityMonitoringUploadFailed:
            return runwayColors.statusRed;
        case TimelineEventType.ReviewSubmissionItemStatusUpdated: {
            const data = timelineEvent.eventData as TimelineEventReleaseSubmissionItemStatusUpdatedData;

            switch (data?.item?.state) {
                case AppStoreReviewSubmissionItemState.ReadyForReview:
                case AppStoreReviewSubmissionItemState.Removed:
                    return runwayColors.statusGrey;
                case AppStoreReviewSubmissionItemState.Accepted:
                    return runwayColors.statusYellow;
                case AppStoreReviewSubmissionItemState.Approved:
                    return runwayColors.statusGreen;
                case AppStoreReviewSubmissionItemState.Rejected:
                    return runwayColors.statusRed;
            }
            return runwayColors.statusGreen;
        }
        case TimelineEventType.ReleaseStepStagedScreenshotsApplied: {
            const data = timelineEvent.eventData as TimelineEventReleaseStepStagedScreenshotsAppliedData;
            return data?.failedCount ? runwayColors.statusRed : runwayColors.statusGreen;
        }
        case TimelineEventType.ReleaseStepRCBuildStatusChanged: {
            const data = timelineEvent.eventData as TimelineEventReleaseStepRCBuildStatusChangedData;
            switch (data?.buildStatus) {
                case BuildStatus.Success:
                    return runwayColors.statusGreen;
                case BuildStatus.Failure:
                    return runwayColors.statusRed;
                case BuildStatus.Stopped:
                case BuildStatus.InProgress:
                    return runwayColors.statusYellow;
            }
            break;
        }
        case TimelineEventType.ReleaseStepCIBuildFailureOverridden: {
            const data = timelineEvent.eventData as TimelineEventReleaseStepCIBuildFailureOverriddenData;
            return data?.overridden ? runwayColors.statusGreen : antGrey.grey6;
        }

        case TimelineEventType.AppStoreVersionStatusUpdated: {
            const data = timelineEvent.eventData as TimelineEventAppStoreVersionStatusUpdatedData;

            switch (data?.appStoreStatus) {
                case AppleAppStoreState.ReadyForSale:
                case AppleAppStoreState.PreorderReadyForSale:
                case AppleAppStoreState.ReplacedWithNewVersion:
                case GooglePlayStoreState.Completed:
                case AppleAppStoreState.ReadyForReview:
                case AppleAppStoreState.PrepareForSubmission:
                    return runwayColors.statusGreen;
                case AppleAppStoreState.ProcessingForAppStore:
                case AppleAppStoreState.PendingDeveloperRelease:
                case AppleAppStoreState.PendingAppleRelease:
                case AppleAppStoreState.InReview:
                case AppleAppStoreState.WaitingForReview:
                case AppleAppStoreState.WaitingForExportCompliance:
                case AppleAppStoreState.PendingContract:
                case GooglePlayStoreState.InProgress:
                    return runwayColors.statusYellow;
                case AppleAppStoreState.DeveloperRejected:
                case AppleAppStoreState.Rejected:
                case AppleAppStoreState.MetadataRejected:
                case AppleAppStoreState.InvalidBinary:
                case GooglePlayStoreState.Halted:
                case AppleAppStoreState.DeveloperRemovedFromSale:
                case AppleAppStoreState.RemovedFromSale:
                    return runwayColors.statusRed;
                case GooglePlayStoreState.StatusUnspecified:
                case GooglePlayStoreState.Draft:
                    return antGrey.grey4;
            }

            return runwayColors.statusGreen;
        }
        case TimelineEventType.HealthMetricsSkippedForStaleData:
            return runwayColors.statusYellow;
        case TimelineEventType.AppStoreVersionPhasedReleaseUpdated: {
            const data = timelineEvent.eventData as TimelineEventAppStoreVersionPhasedReleaseUpdatedData;

            switch (data?.phaseState) {
                case AppleAppStorePhasedReleaseState.Inactive:
                case GooglePlayStoreState.StatusUnspecified:
                case GooglePlayStoreState.Draft:
                    return antGrey.grey6;
                case AppleAppStorePhasedReleaseState.Active:
                case GooglePlayStoreState.InProgress:
                case GooglePlayStoreState.Completed:
                case AppleAppStorePhasedReleaseState.Complete:
                    return runwayColors.statusGreen;
                case AppleAppStorePhasedReleaseState.Paused:
                case GooglePlayStoreState.Halted:
                    return runwayColors.statusYellow;
            }

            return runwayColors.statusGreen;
        }
        case TimelineEventType.ChecklistItemStatusUpdated: {
            const data = timelineEvent.eventData as TimelineEventChecklistItemStatusUpdatedData;
            if (data?.checklistItemStatus) {
                return getTimelineColorForChecklistItemStatusType(data.checklistItemStatus);
            }
            return antGrey.grey6;
        }
        case TimelineEventType.ReleaseStepStatusChanged: {
            const data = timelineEvent.eventData as TimelineEventReleaseStepStatusChangedData;

            switch (data?.releaseStepStatus) {
                case ReleaseStepStatus.Inactive:
                case ReleaseStepStatus.Skipped:
                    return antGrey.grey6;
                case ReleaseStepStatus.PendingChecklistOnly:
                case ReleaseStepStatus.Pending:
                    return runwayColors.statusYellow;
                case ReleaseStepStatus.Blocked:
                    return runwayColors.statusRed;
                case ReleaseStepStatus.Ready:
                default:
                    return runwayColors.statusGreen;
            }
        }
        case TimelineEventType.ReleaseStepRegressionTestingStatusChanged: {
            const data = timelineEvent.eventData as TimelineEventReleaseStepRegressionTestingStatusChangedData;

            switch (data?.regressionTestingStatus) {
                case RegressionStatus.NotStarted:
                    return antGrey.grey4;
                case RegressionStatus.InProgress:
                    return runwayColors.statusYellow;
                case RegressionStatus.Failed:
                    return runwayColors.statusRed;
                case RegressionStatus.Passed:
                default:
                    return runwayColors.statusGreen;
            }
        }
        case TimelineEventType.BatchActionRequested: {
            const data = timelineEvent.eventData as TimelineEventBatchActionRequestedData;

            const { lastAttemptedAt, anySuccessful, anyPendingRetry, anyFailed, retryExpected } =
                batchActionSummaryInfosFromBatchTimelineEventData(data);

            if ((!lastAttemptedAt && !(anySuccessful || anyPendingRetry || anyFailed)) || retryExpected) {
                return runwayColors.statusYellow;
            }

            return anySuccessful ? runwayColors.statusGreen : runwayColors.statusRed;
        }
        case TimelineEventType.AutoSelectLatestBuildsAutomation:
        case TimelineEventType.AutoSelectLatestExpoUpdateGroupAutomation:
        case TimelineEventType.AutoCreateIncidentsAutomation: {
            const data = timelineEvent.automationData;
            switch (data?.automationStatus) {
                case AutomationStatus.Active:
                case AutomationStatus.Success:
                    return runwayColors.statusGreen;
                case AutomationStatus.Pending:
                case AutomationStatus.Skipped:
                case AutomationStatus.Paused:
                case AutomationStatus.Retrying:
                case AutomationStatus.InProgress:
                    return runwayColors.statusYellow;
                case AutomationStatus.Failed:
                case AutomationStatus.Incomplete:
                case AutomationStatus.Blocked:
                    return runwayColors.statusRed;
            }
        }
    }

    return color;
};

const getTimelineIconForEvent = (props: { timelineEvent: TimelineEvent }) => {
    let component = CheckCircleOutlined;

    switch (props.timelineEvent.type) {
        case TimelineEventType.ReviewSubmissionItemAdded:
            return PlusOutlined;
        case TimelineEventType.ReviewSubmissionItemRemoved:
            return MinusOutlined;
        case TimelineEventType.ReleaseCodePromoted:
        case TimelineEventType.PRMerged:
        case TimelineEventType.ReleaseBranchBackmergePerformed:
            return PullRequestOutlined;
        case TimelineEventType.ReleaseBranchCreated:
            return BranchesOutlined;
        case TimelineEventType.ReleaseKickedOff:
            return PlayCircleOutlined;
        case TimelineEventType.VCSCherryPickConflict:
        case TimelineEventType.VCSCherryPickError:
        case TimelineEventType.VCSCherryPickPRCreated:
            return MergeOutlined;
        case TimelineEventType.TargetReleaseDateUpcoming:
        case TimelineEventType.TargetSubmissionDateUpcoming:
        case TimelineEventType.TargetKickoffDateUpcoming:
        case TimelineEventType.AutoCreateRunwayReleasesUpcoming:
        case TimelineEventType.ReleaseTargetKickoffDateUpdated:
        case TimelineEventType.ReleaseTargetSubmissionDateUpdated:
        case TimelineEventType.ReleaseTargetReleaseDateUpdated:
        case TimelineEventType.ScheduleCadenceDisabled:
            return CalendarOutlined;
        case TimelineEventType.AppStoreVersionBetaSubmittedForReview:
            return SearchOutlined;
        case TimelineEventType.ReleaseStepBetaSoakStarted:
        case TimelineEventType.BuildResignStarted:
            return ClockCircleOutlined;
        case TimelineEventType.AppStoreVersionReleaseNotesUpdated:
            return FileTextOutlined;
        case TimelineEventType.AppStoreVersionAppStoreBuildChanged:
            return TabletOutlined;
        case TimelineEventType.ChecklistItemCommentUpdated:
        case TimelineEventType.CommsChannelCreated:
            return MessageOutlined;
        case TimelineEventType.ChecklistItemDueUpcoming:
            return CheckSquareOutlined;
        case TimelineEventType.ReleaseTagged:
        case TimelineEventType.BuildDetailsPostedToTicket:
            return TagOutlined;
        case TimelineEventType.ReleasePilotChanged:
        case TimelineEventType.CommsGroupsSynced:
        case TimelineEventType.CommsPilotUserGroupSynced:
        case TimelineEventType.JiraIssueTrackingIsMissing:
            return TeamOutlined;
        case TimelineEventType.ReleaseVersionUpdated:
        case TimelineEventType.ReleaseVersionBumped:
        case TimelineEventType.ReleaseStepBetaSoakFinished:
        case TimelineEventType.ReleaseCreated:
        case TimelineEventType.ReleaseCreatedUpcomingRelease:
        case TimelineEventType.ReleaseReleased:
        case TimelineEventType.ReleaseSubmitted:
        case TimelineEventType.ReleaseStepsReadyToSubmit:
        case TimelineEventType.ReleaseStepsReadyToRelease:
        case TimelineEventType.ReleaseStepMetadataApprovalChanged:
        case TimelineEventType.ReleaseStepTranslationsApprovalChanged:
        case TimelineEventType.ReleaseStepScreenshotsApprovalChanged:
        case TimelineEventType.ReleaseStepChecklistCompleted:
        case TimelineEventType.AndroidRolloutStartedManually:
        case TimelineEventType.AndroidRolloutStartedFromForwardedEmail:
        case TimelineEventType.BuildResignCompleted:
        case TimelineEventType.ResignSequenceCompleted:
        case TimelineEventType.TranslationsUploaded:
        case TimelineEventType.TranslationsExported:
        case TimelineEventType.TranslationsUpdated:
        case TimelineEventType.ReleaseCompleted:
        case TimelineEventType.ReleaseMarkedActive:
        case TimelineEventType.RolloutSuperseded:
            return CheckCircleOutlined;
        case TimelineEventType.ReleaseUncompleted:
        case TimelineEventType.ReleaseUnskipped:
            return UndoOutlined;
        case TimelineEventType.SyncedVCSMetadata:
            return SyncOutlined;
        // Staging only writes to Runway, so no cloud icon
        case TimelineEventType.ReleaseStepStagedScreenshotAdded:
            return UploadOutlined;
        case TimelineEventType.ReleaseStepStagedScreenshotsApplied: {
            const data = props.timelineEvent.eventData as TimelineEventReleaseStepStagedScreenshotsAppliedData;
            return data?.deletedCount ? CloudSyncOutlined : CloudUploadOutlined;
        }
        case TimelineEventType.ReleaseStepScreenshotAdded:
        case TimelineEventType.BinaryArtifactAppStoreUploadSuccess:
        case TimelineEventType.DsymArtifactStabilityMonitoringUploadSuccess:
        case TimelineEventType.VCSReleaseAssetsUploaded:
        case TimelineEventType.MetadataTranslationsUploaded:
            return CloudUploadOutlined;
        case TimelineEventType.ReleaseStepScreenshotDeleted:
        case TimelineEventType.ReleaseMarkedRejected:
            return DeleteOutlined;
        case TimelineEventType.ReleaseStepScreenshotsReordered:
            return SwapOutlined;
        case TimelineEventType.ReleaseSchedulePaused:
            return PauseCircleOutlined;
        case TimelineEventType.ReleaseScheduleResumed:
            return PlayCircleOutlined;
        case TimelineEventType.BinaryArtifactAppStoreUploadFailed:
        case TimelineEventType.DsymArtifactStabilityMonitoringUploadFailed:
            return CloseCircleOutlined;
        case TimelineEventType.ResignSequenceStarted:
            return KeyOutlined;
        case TimelineEventType.MetadataTranslationsExported:
            return CloudDownloadOutlined;
        case TimelineEventType.ReviewSubmissionItemStatusUpdated: {
            const data = props.timelineEvent.eventData as TimelineEventReleaseSubmissionItemStatusUpdatedData;

            switch (data?.item?.state) {
                case AppStoreReviewSubmissionItemState.ReadyForReview:
                case AppStoreReviewSubmissionItemState.Accepted:
                case AppStoreReviewSubmissionItemState.Approved:
                    return CheckCircleOutlined;
                case AppStoreReviewSubmissionItemState.Rejected:
                    return CloseCircleOutlined;
                case AppStoreReviewSubmissionItemState.Removed:
                    return MinusCircleOutlined;
            }
            break;
        }
        case TimelineEventType.ReleaseStepRCBuildStatusChanged: {
            const data = props.timelineEvent.eventData as TimelineEventReleaseStepRCBuildStatusChangedData;
            switch (data?.buildStatus) {
                case BuildStatus.Success:
                    return CheckCircleOutlined;
                case BuildStatus.Failure:
                    return CloseCircleOutlined;
                case BuildStatus.Stopped:
                    return PauseCircleOutlined;
                case BuildStatus.InProgress:
                    return ClockCircleOutlined;
            }
            break;
        }
        case TimelineEventType.ReleaseStepCIBuildFailureOverridden: {
            const data = props.timelineEvent.eventData as TimelineEventReleaseStepCIBuildFailureOverriddenData;
            return data?.overridden ? CheckCircleOutlined : CloseCircleOutlined;
        }

        case TimelineEventType.AppStoreVersionStatusUpdated: {
            const data = props.timelineEvent.eventData as TimelineEventAppStoreVersionStatusUpdatedData;

            switch (data?.appStoreStatus) {
                case AppleAppStoreState.ReadyForSale:
                case AppleAppStoreState.PreorderReadyForSale:
                case AppleAppStoreState.ReplacedWithNewVersion:
                case GooglePlayStoreState.Completed:
                    return CheckCircleOutlined;
                case AppleAppStoreState.ProcessingForAppStore:
                case AppleAppStoreState.PendingDeveloperRelease:
                case AppleAppStoreState.PendingAppleRelease:
                case AppleAppStoreState.InReview:
                case AppleAppStoreState.WaitingForReview:
                case AppleAppStoreState.WaitingForExportCompliance:
                case AppleAppStoreState.PendingContract:
                case GooglePlayStoreState.InProgress:
                case AppleAppStoreState.ReadyForReview:
                case AppleAppStoreState.PrepareForSubmission:
                    return ClockCircleOutlined;
                case AppleAppStoreState.DeveloperRejected:
                case AppleAppStoreState.Rejected:
                case AppleAppStoreState.MetadataRejected:
                case AppleAppStoreState.InvalidBinary:
                case GooglePlayStoreState.Halted:
                case AppleAppStoreState.DeveloperRemovedFromSale:
                case AppleAppStoreState.RemovedFromSale:
                case GooglePlayStoreState.StatusUnspecified:
                case GooglePlayStoreState.Draft:
                    return CloseCircleOutlined;
            }
            break;
        }
        case TimelineEventType.HealthMetricsSkippedForStaleData:
            return ClockCircleOutlined;
        case TimelineEventType.AppStoreVersionPhasedReleaseUpdated: {
            const data = props.timelineEvent.eventData as TimelineEventAppStoreVersionPhasedReleaseUpdatedData;

            switch (data?.phaseState) {
                case AppleAppStorePhasedReleaseState.Inactive:
                case GooglePlayStoreState.StatusUnspecified:
                case GooglePlayStoreState.Draft:
                    return CloseCircleOutlined;
                case AppleAppStorePhasedReleaseState.Active:
                case GooglePlayStoreState.InProgress:
                    return SignalFilled;
                case GooglePlayStoreState.Completed:
                case AppleAppStorePhasedReleaseState.Complete:
                    return CheckCircleOutlined;
                case AppleAppStorePhasedReleaseState.Paused:
                case GooglePlayStoreState.Halted:
                    return PauseCircleOutlined;
            }
            return CheckCircleOutlined;
        }
        case TimelineEventType.ChecklistItemStatusUpdated: {
            const data = props.timelineEvent.eventData as TimelineEventChecklistItemStatusUpdatedData;
            if (data?.checklistItemStatus) {
                return getTimelineIconForChecklistItemStatusType(data.checklistItemStatus);
            }
            return CloseCircleOutlined;
        }
        case TimelineEventType.ReleaseStepStatusChanged: {
            const data = props.timelineEvent.eventData as TimelineEventReleaseStepStatusChangedData;

            switch (data?.releaseStepStatus) {
                case ReleaseStepStatus.Inactive:
                case ReleaseStepStatus.Blocked:
                case ReleaseStepStatus.Skipped:
                    return CloseCircleOutlined;
                case ReleaseStepStatus.PendingChecklistOnly:
                case ReleaseStepStatus.Pending:
                    return ClockCircleOutlined;
                case ReleaseStepStatus.Ready:
                default:
                    return CheckCircleOutlined;
            }
        }
        case TimelineEventType.ReleaseStepRegressionTestingStatusChanged: {
            const data = props.timelineEvent.eventData as TimelineEventReleaseStepRegressionTestingStatusChangedData;

            switch (data?.regressionTestingStatus) {
                case RegressionStatus.NotStarted:
                case RegressionStatus.Failed:
                    return CloseCircleOutlined;
                case RegressionStatus.InProgress:
                    return ClockCircleOutlined;
                case RegressionStatus.Passed:
                default:
                    return CheckCircleOutlined;
            }
        }
        case TimelineEventType.AutomationSettingsUpdated:
        case TimelineEventType.NotificationSettingsUpdated:
        case TimelineEventType.ReleaseSettingsUpdated:
        case TimelineEventType.ReviewDetailsUpdated:
            return SettingOutlined;
        case TimelineEventType.AutomationEnabled:
            return ThunderboltOutlined;
        case TimelineEventType.AutomationDisabled:
        case TimelineEventType.NotificationDisabled:
        case TimelineEventType.NotificationStepOverrideDeleted:
            return CloseOutlined;
        case TimelineEventType.NotificationEnabled:
            return NotificationOutlined;
        case TimelineEventType.ReleaseDeleted:
        case TimelineEventType.ReleaseBranchDeleted:
            return DeleteRowOutlined;
        case TimelineEventType.ReleaseSkipped:
            return NodeExpandOutlined;
        case TimelineEventType.FeatureWorkItemIgnored:
            return EyeInvisibleOutlined;
        case TimelineEventType.FeatureWorkItemUnIgnored:
            return EyeOutlined;
        case TimelineEventType.FixRequestAdded:
        case TimelineEventType.FixRequestDeleted:
        case TimelineEventType.FixRequestApprovalStatusUpdated:
        case TimelineEventType.FixMoved:
        case TimelineEventType.BuildWorkflowTriggered:
            return ToolOutlined;
        case TimelineEventType.TriageIssueDetected:
        case TimelineEventType.TriageIssueStatusUpdated:
        case TimelineEventType.TriageIssueSourceAdded:
        case TimelineEventType.TriageIssueSourceRemoved:
        case TimelineEventType.TriageIssueUnmerged:
            return ExclamationCircleOutlined;
        case TimelineEventType.TriageIssueAssigneeChanged:
            return UserSwitchOutlined;
        case TimelineEventType.TriageIssueTicketCreated:
            return TagOutlined;
        case TimelineEventType.StagedRolloutUpdated:
            return RiseOutlined;
        case TimelineEventType.IssueTrackingReleaseCompleted:
        case TimelineEventType.ReleaseSummarySent:
            return FileDoneOutlined;
        case TimelineEventType.IntegrationAdded:
        case TimelineEventType.IntegrationUpdated:
            return ApiOutlined;
        case TimelineEventType.IntegrationRemoved:
            return DisconnectOutlined;
        case TimelineEventType.ScheduleCadenceEnabled:
            return CarryOutOutlined;
        case TimelineEventType.AppStoreVersionCreated:
            return FieldBinaryOutlined;
        case TimelineEventType.BuildEncryptionDefaultsApplied:
            return SafetyOutlined;
        case TimelineEventType.BetaBuildTestingGroupsApplied:
            return UsergroupAddOutlined;
        case TimelineEventType.BetaBuildPromotedToTestTrack:
            return ToTopOutlined;
        case TimelineEventType.BetaBuildTestingNotesAutoApplied:
            return FileSearchOutlined;
        case TimelineEventType.CIBuildDiffSummaryGenerated:
            return DiffOutlined;
        case TimelineEventType.AutoDownloadArtifacts:
            return DownloadOutlined;
        case TimelineEventType.FeatureAffiliationsApplied:
        case TimelineEventType.FixLabelsApplied:
        case TimelineEventType.IssueTrackingTicketsTransitionedToStatus:
            return TagsOutlined;
        case TimelineEventType.DefaultReviewAttachmentUpdated:
        case TimelineEventType.ReviewAttachmentUpdated:
        case TimelineEventType.ReviewAttachmentRemoved:
            return PaperClipOutlined;
        case TimelineEventType.AppStoreUsersSynced:
            return UserSwitchOutlined;
        case TimelineEventType.ProvisioningProfileDevicesSynced:
            return IdcardOutlined;
        case TimelineEventType.DevicesSynced:
            return MobileOutlined;
        case TimelineEventType.TestRunCreated:
            return ReconciliationOutlined;
        case TimelineEventType.LocalizableStringsCherryPicked:
            return TranslationOutlined;
        case TimelineEventType.AutoSelectLatestBuildsAutomation:
        case TimelineEventType.AutoSelectLatestExpoUpdateGroupAutomation:
            return TabletOutlined;
        case TimelineEventType.AutoCreateIncidentsAutomation:
            return WarningOutlined;
        case TimelineEventType.BatchActionRequested: {
            const data = props.timelineEvent.eventData as TimelineEventBatchActionRequestedData;

            const { lastAttemptedAt, anySuccessful, anyPendingRetry, anyFailed, retryExpected } =
                batchActionSummaryInfosFromBatchTimelineEventData(data);

            if (!lastAttemptedAt || !(anySuccessful || anyPendingRetry || anyFailed)) {
                return ClockCircleOutlined;
            }

            return (
                retryExpected ? RedoOutlined
                : anySuccessful ? CheckCircleOutlined
                : CloseCircleOutlined
            );
        }
        case TimelineEventType.ReleaseStepIntegrationSettingsUpdated:
    }
    return component;
};

export const TimelineDotContainer: React.FC<React.PropsWithChildren> = ({ children }) => {
    return (
        <div
            style={{
                width: 20,
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    backgroundColor: antGrey.grey4,
                    width: 2,
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'start',
                    position: 'relative',
                }}
            >
                <div
                    className="timeline-event-dot-bottom"
                    style={{
                        position: 'absolute',
                        top: 10,
                        zIndex: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: antGrey.grey4,
                    }}
                />
                <div
                    className="timeline-event-dot-top"
                    style={{
                        position: 'absolute',
                        top: 'calc(-100% + 10px)',
                        zIndex: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: antGrey.grey4,
                    }}
                />

                <div
                    style={{
                        position: 'relative',
                        zIndex: 0,
                        flexShrink: 0,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: runwayColors.white,
                            borderRadius: 20,
                        }}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const automationSettingsIconForStatus: (
    status: AutomationStatus,
    isAutomationRetrying?: boolean,
    isAutomationPending?: boolean,
    isAutomationRecurring?: boolean,
) => React.ReactNode = (status, isAutomationRetrying, isAutomationPending, isAutomationRecurring) => {
    let icon: React.ReactNode;
    const color = colorForAutomationStatus(status, false, isAutomationRetrying, isAutomationPending);

    if (isAutomationRetrying) {
        return <LoadingOutlined spin style={{ color }} />;
    }

    if (isAutomationRecurring && isAutomationPending) {
        return <ExclamationCircleOutlined style={{ color }} />;
    }

    switch (status) {
        case AutomationStatus.Success:
        case AutomationStatus.Active:
            icon = <CheckCircleOutlined style={{ color }} />;
            break;
        case AutomationStatus.Failed:
            icon = <CloseCircleOutlined style={{ color }} />;
            break;
        case AutomationStatus.Pending:
            icon = <ClockCircleOutlined style={{ color }} />;
            break;
        case AutomationStatus.Inactive:
        case AutomationStatus.Skipped:
            icon = <MinusCircleOutlined style={{ color }} />;
            break;
        case AutomationStatus.Incomplete:
            icon = <CloseCircleOutlined style={{ color }} />;
            break;
        case AutomationStatus.Paused:
            icon = <PauseCircleOutlined style={{ color }} />;
            break;
        case AutomationStatus.Blocked:
            icon = <ExclamationCircleOutlined style={{ color }} />;
            break;
        case AutomationStatus.Retrying:
            icon = <RedoOutlined style={{ color }} />;
            break;
        default:
            return null;
    }

    return icon;
};

const TimelineEventItemBase: React.FC<{
    nodeIcon: React.ReactNode;
    eventIcon?: React.ReactNode;
    primaryLabel: React.ReactNode;
    attributionIcon?: React.ReactNode;
    secondaryLabel?: React.ReactNode;
    automationDataTable?: React.ReactNode;
    additionalDataTableForNonAutomations?: React.ReactNode;
    isUsedInStepSummary?: boolean;
    isTargetEvent?: boolean;
    isCompact?: boolean;
}> = ({
    nodeIcon,
    eventIcon,
    primaryLabel,
    attributionIcon,
    secondaryLabel,
    automationDataTable,
    additionalDataTableForNonAutomations,
    isUsedInStepSummary = false,
    isTargetEvent = false,
    isCompact = false,
}) => {
    let itemHeight = 48;
    if (automationDataTable || additionalDataTableForNonAutomations) {
        itemHeight = 35;
    }

    return (
        <>
            <Row
                align="stretch"
                wrap={false}
                style={{
                    minHeight: itemHeight,
                    position: 'relative',
                    alignItems: 'stretch',
                }}
                className="base-timeline-event-item"
            >
                <div
                    className="timeline-event-overlay"
                    style={{
                        opacity: 0,
                    }}
                />
                <Col
                    flex={
                        isUsedInStepSummary || isTargetEvent ? '0 0 10.5rem'
                        : isCompact ?
                            '0 0 5.5rem'
                        :   '0 0 7rem'
                    }
                    style={{
                        textAlign: 'right',
                        marginTop: -2,
                        marginRight: isCompact ? '0.75rem' : '1.25rem',
                    }}
                >
                    {secondaryLabel}
                </Col>

                <Col
                    flex="0 0 1.25rem"
                    style={{
                        marginRight: isCompact ? '0.75rem' : '1.25rem',
                        display: 'flex',
                        alignItems: 'stretch',
                    }}
                >
                    <TimelineDotContainer>{nodeIcon}</TimelineDotContainer>
                </Col>

                {automationDataTable || additionalDataTableForNonAutomations ?
                    <Col style={{ width: '100%' }}>
                        <Row style={{ height: 30 }}>
                            <Col
                                flex="1 1 0"
                                style={{
                                    overflow: 'visible',
                                    minWidth: '2rem',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '0.75rem',
                                }}
                            >
                                {eventIcon}
                                <div
                                    className="timeline-event-primary-label"
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        maxWidth: '100%',
                                    }}
                                >
                                    {primaryLabel}
                                </div>
                            </Col>

                            {attributionIcon && (
                                <Col
                                    flex="0 0 2rem"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    {attributionIcon}
                                </Col>
                            )}
                        </Row>
                        <>{automationDataTable ? automationDataTable : <></>}</>
                        <>{additionalDataTableForNonAutomations ? additionalDataTableForNonAutomations : <></>}</>
                    </Col>
                :   <>
                        <Col
                            flex="1 1 0"
                            style={{
                                overflow: 'visible',
                                minWidth: '2rem',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.75rem',
                            }}
                        >
                            {eventIcon}
                            <div
                                className="timeline-event-primary-label"
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    maxWidth: '100%',
                                }}
                            >
                                {primaryLabel}
                            </div>
                        </Col>

                        {attributionIcon && (
                            <Col
                                flex="0 0 2rem"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start',
                                }}
                            >
                                {attributionIcon}
                            </Col>
                        )}
                    </>
                }
            </Row>
        </>
    );
};

interface TimelineEventItemProps extends AppComponentProps {
    timelineEvent: TimelineEvent;
    appPlatform: AppPlatform;
    appStoreType: AppStoreType;
    releaseStepType?: ReleaseStepType;
    context: ReleaseTimelineSectionContext;
    shouldHideEventCount?: boolean;
    isUsedInStepSummary?: boolean;
    isCompact?: boolean;
}

const BatchActionRequestDetailsCollapse: React.FC<{
    data: TimelineEventBatchActionRequestedData;
    associatedUser: OrgUser | undefined;
    appId: string;
    handleCollapseChange: (key: string | string[]) => void;
    effectiveTimezone: string;
    use24HourTimeFormat: boolean;
}> = ({ data, associatedUser, appId, handleCollapseChange, effectiveTimezone, use24HourTimeFormat }) => {
    const mdmOverride = useMdmPlatformOverride(appId);
    const destinationNames = (dests: Destination[]) =>
        (dests ?? [])
            .map((d) => displayStringForDestination(d.platform, d.appStoreType, d.name, undefined, mdmOverride?.label))
            .join(', ');
    const labelStyle = {
        fontSize: '12px',
        color: antGrey.grey7,
        letterSpacing: '0.1em',
        textTransform: 'uppercase' as const,
        marginRight: 8,
        fontWeight: 400,
    };

    const detailLabelStyle = {
        color: antGrey.grey7,
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        textOverflow: 'ellipsis',
    };

    const detailValueContainerStyle = {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'end',
        gap: 4,
        marginLeft: 16,
    };

    const detailRowStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'start',
    };

    const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
        <div style={detailRowStyle}>
            <Space size={0}>
                <Text type="secondary" style={detailLabelStyle}>
                    {label}
                </Text>
            </Space>
            <div style={detailValueContainerStyle}>
                <Flex gap={10} align="center">
                    {children}
                </Flex>
            </div>
        </div>
    );

    const {
        lastAttemptedAt,
        maxAttempts,
        targetStepDisplayNames,
        successesStepDisplayNames,
        pendingRetryStepDisplayNames,
        failuresStepDisplayNames,
        successfulForDestStepConfigIds,
        pendingRetryForDestStepConfigIds,
        failedForDestStepConfigIds,
        anySuccessful,
        anyPendingRetry,
        anyFailed,
        retryExpected,
    } = batchActionSummaryInfosFromBatchTimelineEventData(data);

    const haveAnyAttemptInfos = anySuccessful || anyPendingRetry || anyFailed;

    const transactionTypeDisplayName = displayNameForTransactionType(data.transactionType);

    const destinations = useSelector((state: RootState) =>
        Object.values(selectDestinationInfoByDestinationStepConfigId(state, appId)),
    );

    const successfulForDests = React.useMemo(() => {
        return anySuccessful ?
                destinations.filter((destination) => successfulForDestStepConfigIds?.includes(destination.stepConfigId))
            :   [];
    }, [anySuccessful, destinations]);

    const pendingRetryForDests = React.useMemo(() => {
        return anyPendingRetry ?
                destinations.filter((destination) =>
                    pendingRetryForDestStepConfigIds?.includes(destination.stepConfigId),
                )
            :   [];
    }, [anyPendingRetry, destinations]);

    const failedForDests = React.useMemo(() => {
        return anyFailed ?
                destinations.filter((destination) => failedForDestStepConfigIds?.includes(destination.stepConfigId))
            :   [];
    }, [failedForDestStepConfigIds, destinations]);

    const targetDests = React.useMemo(() => {
        return destinations ?
                destinations.filter((destination) => data.targetDestStepConfigIds?.includes(destination.stepConfigId))
            :   [];
    }, [data, destinations]);

    const stepGroupName = data.stepGroupName;

    const extraColor =
        !haveAnyAttemptInfos || retryExpected ? runwayColors.statusYellow
        : anySuccessful ? runwayColors.statusGreen
        : runwayColors.statusRed;

    const extraDisplayText =
        !haveAnyAttemptInfos ? 'Requested'
        : retryExpected ? 'Pending retry'
        : anySuccessful && !anyFailed ? 'Successful'
        : anySuccessful ? 'Partially successful'
        : 'Failed';

    const extraIcon =
        !haveAnyAttemptInfos || retryExpected ? <ClockCircleOutlined style={{ color: extraColor }} />
        : anySuccessful ? <CheckCircleOutlined style={{ color: extraColor }} />
        : <CloseCircleOutlined style={{ color: extraColor }} />;

    return (
        <div style={{ marginBottom: 30, maxWidth: 720 }}>
            <Collapse
                onChange={handleCollapseChange}
                collapsible="header"
                defaultActiveKey={[]}
                items={[
                    {
                        key: '1',
                        label: (
                            <Flex align="baseline" style={{ lineHeight: 1 }}>
                                <Text style={labelStyle}>{'Batch action'}</Text>
                                {!!transactionTypeDisplayName && (
                                    <Text type="secondary">{transactionTypeDisplayName}</Text>
                                )}
                            </Flex>
                        ),
                        children: (
                            <Flex vertical gap={16} justify="start">
                                <div>
                                    <div>
                                        <div style={{ ...detailRowStyle, marginBottom: 8 }}>
                                            <Text style={{ ...labelStyle, marginLeft: 5 }}>Request</Text>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            backgroundColor: antGrey.grey2,
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: `1px solid ${antGrey.grey5}`,
                                        }}
                                    >
                                        <Space
                                            direction="vertical"
                                            size={12}
                                            style={{ width: '100%' }}
                                            split={<Divider style={{ backgroundColor: antGrey.grey5, margin: 0 }} />}
                                        >
                                            <DetailRow label="Action type">
                                                <Text
                                                    type="secondary"
                                                    style={{ color: antGrey.grey8, textAlign: 'right' }}
                                                >
                                                    {displayNameForTransactionType(data.transactionType)}
                                                </Text>
                                            </DetailRow>
                                            <DetailRow label="Step group (target steps)">
                                                <Text
                                                    type="secondary"
                                                    style={{ color: antGrey.grey8, textAlign: 'right' }}
                                                >
                                                    {`${stepGroupName}${targetStepDisplayNames?.length > 0 ? ` (${targetStepDisplayNames?.join(', ')})` : ''}`}
                                                </Text>
                                            </DetailRow>
                                            <DetailRow label="Destinations">
                                                <Text
                                                    type="secondary"
                                                    style={{ color: antGrey.grey8, textAlign: 'right' }}
                                                >
                                                    {destinationNames(targetDests)}
                                                </Text>
                                            </DetailRow>
                                            <DetailRow label="Requested by">
                                                {associatedUser && (
                                                    <Avatar
                                                        src={associatedUser.commsAvatars?.image32}
                                                        style={{
                                                            backgroundColor: getAvatarColorForName(
                                                                displayUserNameShort(associatedUser),
                                                            ),
                                                            marginRight: 8,
                                                        }}
                                                        size={20}
                                                    />
                                                )}
                                                <Text type="secondary" style={{ color: antGrey.grey8 }}>
                                                    {associatedUser ? displayUserNameShort(associatedUser) : 'Unknown'}
                                                </Text>
                                            </DetailRow>
                                            {dayjs(data.requestedAt).isValid() && (
                                                <DetailRow label="Requested at">
                                                    <Text type="secondary" style={{ color: antGrey.grey8 }}>
                                                        {formatDateWithTimezone(
                                                            data.requestedAt,
                                                            'shortMonthDateWithTime',
                                                            effectiveTimezone,
                                                            use24HourTimeFormat,
                                                        )}
                                                    </Text>
                                                </DetailRow>
                                            )}
                                        </Space>
                                    </div>
                                </div>
                                {haveAnyAttemptInfos && (
                                    <div>
                                        <div>
                                            <div style={{ ...detailRowStyle, marginBottom: 8 }}>
                                                <Text style={{ ...labelStyle, marginLeft: 5 }}>Results</Text>
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                backgroundColor: antGrey.grey2,
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: `1px solid ${antGrey.grey5}`,
                                            }}
                                        >
                                            <Space
                                                direction="vertical"
                                                size={12}
                                                style={{ width: '100%' }}
                                                split={
                                                    <Divider style={{ backgroundColor: antGrey.grey5, margin: 0 }} />
                                                }
                                            >
                                                <DetailRow label="Succeeded for target steps">
                                                    {anySuccessful ?
                                                        <Text
                                                            type="secondary"
                                                            style={{ color: antGrey.grey8, textAlign: 'right' }}
                                                        >
                                                            {(successesStepDisplayNames ?? []).join(', ')}
                                                        </Text>
                                                    :   <Text
                                                            type="secondary"
                                                            style={{ color: antGrey.grey6, textAlign: 'right' }}
                                                        >
                                                            {retryExpected ? 'None yet' : 'None'}
                                                        </Text>
                                                    }
                                                </DetailRow>
                                                <DetailRow label="Succeeded for destinations">
                                                    <Text
                                                        type="secondary"
                                                        style={{ color: antGrey.grey8, textAlign: 'right' }}
                                                    >
                                                        {anySuccessful ?
                                                            <>{destinationNames(successfulForDests)}</>
                                                        :   <Text
                                                                type="secondary"
                                                                style={{ color: antGrey.grey6, textAlign: 'right' }}
                                                            >
                                                                {retryExpected ? 'None yet' : 'None'}
                                                            </Text>
                                                        }
                                                    </Text>
                                                </DetailRow>
                                                {anyPendingRetry && (
                                                    <>
                                                        <DetailRow label="Pending retry for target steps">
                                                            <Text
                                                                type="secondary"
                                                                style={{ color: antGrey.grey8, textAlign: 'right' }}
                                                            >
                                                                {(pendingRetryStepDisplayNames ?? []).join(', ')}
                                                            </Text>
                                                        </DetailRow>
                                                        <DetailRow label={`Pending retry for destinations`}>
                                                            <Text
                                                                type="secondary"
                                                                style={{ color: antGrey.grey8, textAlign: 'right' }}
                                                            >
                                                                {destinationNames(pendingRetryForDests)}
                                                            </Text>
                                                        </DetailRow>
                                                    </>
                                                )}
                                                {anyFailed && (
                                                    <>
                                                        <DetailRow
                                                            label={`Failed after ${maxAttempts} retries for target steps`}
                                                        >
                                                            <Text
                                                                type="secondary"
                                                                style={{ color: antGrey.grey8, textAlign: 'right' }}
                                                            >
                                                                {(failuresStepDisplayNames ?? []).join(', ')}
                                                            </Text>
                                                        </DetailRow>
                                                        <DetailRow
                                                            label={`Failed after ${maxAttempts} retries for destinations`}
                                                        >
                                                            <Text
                                                                type="secondary"
                                                                style={{ color: antGrey.grey8, textAlign: 'right' }}
                                                            >
                                                                {destinationNames(failedForDests)}
                                                            </Text>
                                                        </DetailRow>
                                                    </>
                                                )}
                                                {!!lastAttemptedAt && dayjs(lastAttemptedAt).isValid() && (
                                                    <DetailRow label="Last attempted at">
                                                        <Text type="secondary" style={{ color: antGrey.grey8 }}>
                                                            {formatDateWithTimezone(
                                                                lastAttemptedAt,
                                                                'shortMonthDateWithTime',
                                                                effectiveTimezone,
                                                                use24HourTimeFormat,
                                                            )}
                                                        </Text>
                                                    </DetailRow>
                                                )}
                                            </Space>
                                        </div>
                                    </div>
                                )}
                            </Flex>
                        ),
                        extra: (
                            <Flex align="center" gap={8}>
                                {targetDests.length > 0 && (
                                    <div>
                                        <DestinationCounter
                                            destinations={targetDests}
                                            size={22}
                                            platformLabelOverride={mdmOverride?.label}
                                            platformIconOverride={mdmOverride?.icon}
                                        />
                                    </div>
                                )}
                                <Text
                                    style={{
                                        color: extraColor,
                                        maxWidth: 370,
                                    }}
                                    ellipsis={{ tooltip: true }}
                                    strong={true}
                                >
                                    {extraDisplayText}
                                </Text>
                                {extraIcon}
                            </Flex>
                        ),
                    },
                ]}
                bordered
                style={{ background: runwayColors.white }}
            />
        </div>
    );
};

const IntegrationSettingsUpdateCollapse: React.FC<{
    settingsData: TimelineEventIntegrationUpdatedData;
    associatedUser: OrgUser | undefined;
    orgId: string;
    appId: string;
    isStepLevel: boolean;
    handleCollapseChange: (key: string | string[]) => void;
}> = ({ settingsData, associatedUser, orgId, appId, isStepLevel, handleCollapseChange }) => {
    const labelStyle = {
        fontSize: '12px',
        color: antGrey.grey7,
        letterSpacing: '0.1em',
        textTransform: 'uppercase' as const,
        marginRight: 8,
        fontWeight: 400,
    };

    const detailLabelStyle = {
        color: antGrey.grey7,
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        textOverflow: 'ellipsis',
    };

    const detailValueContainerStyle = {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'end',
        gap: 4,
        marginLeft: 16,
    };

    const detailRowStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'start',
    };

    const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
        <div style={detailRowStyle}>
            <Space size={0}>
                <Text type="secondary" style={detailLabelStyle}>
                    {label}
                </Text>
            </Space>
            <div style={detailValueContainerStyle}>
                <Space size={0}>{children}</Space>
            </div>
        </div>
    );

    const formatProviderDisplay = (value: string | undefined, prefix: string) => {
        if (!value) return `${prefix} not set`;
        return `${prefix} '${displayNameForIntegrationType(value as IntegrationType)}'`;
    };

    const hasDetailedSettings = settingsData.previousSettingValue && settingsData.updatedSettingValue;

    return (
        <div style={{ marginBottom: 30, maxWidth: 720 }}>
            <Collapse
                onChange={handleCollapseChange}
                collapsible="header"
                defaultActiveKey={[]}
                items={[
                    {
                        key: '1',
                        label: (
                            <>
                                <Text style={labelStyle}>{isStepLevel ? 'Step' : 'App Settings'}</Text>
                                {isStepLevel && <Text type="secondary">{settingsData.stepName}</Text>}
                            </>
                        ),
                        children: (
                            <div>
                                <div>
                                    <div style={{ ...detailRowStyle, marginBottom: 8 }}>
                                        <Text style={{ ...labelStyle, marginLeft: 5 }}>Details</Text>
                                    </div>
                                </div>
                                <div
                                    style={{
                                        backgroundColor: antGrey.grey2,
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: `1px solid ${antGrey.grey5}`,
                                    }}
                                >
                                    <Space
                                        direction="vertical"
                                        size={12}
                                        style={{ width: '100%' }}
                                        split={<Divider style={{ backgroundColor: antGrey.grey5, margin: 0 }} />}
                                    >
                                        <DetailRow label="Changed by">
                                            {associatedUser && (
                                                <Avatar
                                                    src={associatedUser.commsAvatars?.image32}
                                                    style={{
                                                        backgroundColor: getAvatarColorForName(
                                                            displayUserNameShort(associatedUser),
                                                        ),
                                                        marginRight: 8,
                                                    }}
                                                    size={20}
                                                />
                                            )}
                                            <Text type="secondary" style={{ color: antGrey.grey8 }}>
                                                {associatedUser ?
                                                    displayUserNameShort(associatedUser)
                                                :   settingsData.changedByUser || 'Unknown'}
                                            </Text>
                                        </DetailRow>
                                        <DetailRow label={isStepLevel ? 'Setting name' : 'Integration type'}>
                                            <Text type="secondary" style={{ color: antGrey.grey8 }}>
                                                {`${displayNameForCapability(settingsData.integrationCapability)} integration`}
                                            </Text>
                                        </DetailRow>
                                        {(isStepLevel || hasDetailedSettings) && (
                                            <>
                                                <DetailRow label="New setting">
                                                    <Text type="secondary" style={{ color: antGrey.grey8 }}>
                                                        {isStepLevel ?
                                                            formatProviderDisplay(
                                                                settingsData.updatedSettingValue,
                                                                'Provider is',
                                                            )
                                                        :   settingsData.updatedSettingValue}
                                                    </Text>
                                                </DetailRow>
                                                <DetailRow label="Previous setting">
                                                    <Text type="secondary" style={{ color: antGrey.grey8 }}>
                                                        {isStepLevel ?
                                                            formatProviderDisplay(
                                                                settingsData.previousSettingValue,
                                                                'Provider was',
                                                            )
                                                        :   settingsData.previousSettingValue}
                                                    </Text>
                                                </DetailRow>
                                            </>
                                        )}
                                        <div style={{ alignItems: 'right' }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'end',
                                                    gap: 4,
                                                }}
                                            >
                                                <Text
                                                    type="secondary"
                                                    onClick={() => {
                                                        window.location.href = createAppSettingsRouteWithSettingType(
                                                            orgId,
                                                            appId,
                                                            isStepLevel ?
                                                                SettingType.WorkflowManagement
                                                            :   SettingType.Integrations,
                                                        );
                                                    }}
                                                    style={{
                                                        paddingRight: 0,
                                                        textDecorationLine: 'underline',
                                                        textDecorationStyle: 'dotted',
                                                        textUnderlineOffset: 3,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {isStepLevel ? 'View/edit step settings' : 'View/edit integrations'}
                                                </Text>
                                            </div>
                                        </div>
                                    </Space>
                                </div>
                            </div>
                        ),
                        extra: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ApiOutlined style={{ fontSize: 14, color: antGrey.grey7 }} />
                                <Text type="secondary">Integrations</Text>
                            </div>
                        ),
                    },
                ]}
                bordered
                style={{ background: runwayColors.white }}
            />
        </div>
    );
};

// Renders the auto-created PM ticket's event title with only the ticket key linking out to its tracker.
const TicketCreatedEventTitle: React.FC<{ title: string; data: TimelineEventTriageIssueTicketCreatedData }> = ({
    title,
    data,
}) => {
    if (!data.ticketUrl || !data.ticketKey || !title.includes(data.ticketKey)) {
        return <>{title}</>;
    }
    const keyIndex = title.indexOf(data.ticketKey);
    return (
        <>
            {title.slice(0, keyIndex)}
            <Typography.Link
                href={data.ticketUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
            >
                {data.ticketKey}
            </Typography.Link>
            {title.slice(keyIndex + data.ticketKey.length)}
        </>
    );
};

// Override the plain title for specific events (e.g. to add a link).
const titleContentForEvent = (timelineEvent: TimelineEvent, title: string): React.ReactNode => {
    switch (timelineEvent.type) {
        case TimelineEventType.TriageIssueTicketCreated:
            return (
                <TicketCreatedEventTitle
                    title={title}
                    data={timelineEvent.eventData as TimelineEventTriageIssueTicketCreatedData}
                />
            );
        default:
            return title;
    }
};

export const TimelineEventItem: React.FC<TimelineEventItemProps> = ({
    timelineEvent,
    orgId,
    appId,
    appPlatform,
    appStoreType,
    releaseStepType,
    context,
    shouldHideEventCount,
    isUsedInStepSummary = false,
    isCompact = false,
}) => {
    const user = useSelector((state: RootState) => selectUserById(state, orgId, appId, timelineEvent.userId ?? ''));
    const stepGraphStepsMap = useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, appId));
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));
    const destinationsMap = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const mdmOverride = useMdmPlatformOverride(appId);

    const associatedUser = useSelector((state: RootState) => {
        const associatedUserId = getAssociatedUser(timelineEvent);

        if (associatedUserId) {
            return selectOrgUser(state, orgId, associatedUserId);
        }

        return null;
    });

    const stepRunId = timelineEvent.stepRunId;
    const stepRun = useSelector((state: RootState) => (stepRunId ? selectStepRun(state, stepRunId) : undefined));
    const workflowId = useSelector((state: RootState) => (stepRunId ? selectWorkflowId(state, appId) : undefined));

    const [retryAutomation, { data: retryAutomationResponse, isLoading: retryAutomationIsLoading }] =
        useRetryAutomationMutation();
    const success = retryAutomationResponse?.success;
    const retryAutomationError = retryAutomationResponse?.errorMessage;

    const isEventRelatedToAutomationRun = !!timelineEvent.automationData && stepRun?.automationRuns?.length;
    const automationRun =
        isEventRelatedToAutomationRun ?
            stepRun.automationRuns.find((r) => r.type === timelineEvent.automationData.automationType)
        :   null;
    const shouldDisplayRetryButton = timelineEvent.isRetryable;
    const isAutomationRetrying = automationRun?.isRetrying || timelineEvent.isRetrying || retryAutomationIsLoading;
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));
    const isOrgTimeline = context === ReleaseTimelineSectionContext.OrgTimeline;
    const appName = useSelector((state: RootState) => (isOrgTimeline ? selectAppName(state, appId) : undefined));

    const [isCollapseExpanded, setIsCollapseExpanded] = React.useState(false);
    const handleCollapseChange = () => {
        setIsCollapseExpanded(!isCollapseExpanded);
    };

    const [isSettingsModalOpened, setIsSettingsModalOpened] = React.useState(false);
    const handleSettingsModalOpenedChange = () => {
        setIsSettingsModalOpened(!isSettingsModalOpened);
    };

    function renderTimelineValue(value: any) {
        const conditionData = value as TimelineEventAutomationConditionsData;
        if (conditionData && typeof conditionData.text === 'string' && Array.isArray(conditionData.tooltip)) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                        style={{
                            ...(isAutomationPending ?
                                {
                                    color: runwayColors.statusYellow,
                                }
                            :   {}),
                        }}
                    >
                        {conditionData.text}
                    </span>
                    <Tooltip
                        placement="left"
                        overlayStyle={{
                            maxWidth: '650px',
                            width: 'auto',
                        }}
                        overlayInnerStyle={{
                            borderRadius: '4px',
                            padding: '12px 16px',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                        }}
                        title={
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <div
                                    style={{
                                        fontSize: '12px',
                                        color: antGrey.grey6,
                                        letterSpacing: '0.1em',
                                        marginBottom: 4,
                                        fontWeight: 500,
                                    }}
                                >
                                    {conditionData.tooltipType === AutomationConditionTooltipType.Conditions ?
                                        'EXECUTION CONDITIONS'
                                    :   'RELATED AUTOMATIONS'}
                                </div>
                                {conditionData.tooltip?.map((item: TimelineEventTooltipLine, index: number) =>
                                    conditionData.tooltipType === AutomationConditionTooltipType.Conditions ?
                                        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                            {item.result === AutomationConditionResult.Passed && (
                                                <CheckCircleFilled
                                                    style={{ color: runwayColors.statusGreen, marginTop: 4 }}
                                                />
                                            )}
                                            {item.result === AutomationConditionResult.Failed && (
                                                <CloseCircleFilled
                                                    style={{ color: runwayColors.statusRed, marginTop: 4 }}
                                                />
                                            )}
                                            {item.result === AutomationConditionResult.DoesNotApply && (
                                                <MinusCircleFilled style={{ color: antGrey.grey6, marginTop: 4 }} />
                                            )}
                                            <span>{item.description}</span>
                                        </div>
                                    :   <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 8,
                                                    flexWrap: 'nowrap',
                                                }}
                                            >
                                                <ThunderboltFilled
                                                    style={{
                                                        color: runwayColors.statusGrey,
                                                        marginTop: 5,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <span style={{ flex: '1', wordBreak: 'break-word' }}>{item.name}</span>
                                                <span style={{ flexShrink: 0 }}></span>
                                                <div style={{ marginLeft: 'auto' }}>
                                                    {' '}
                                                    {item.isAutomationEnabled ?
                                                        <div>
                                                            <CheckCircleOutlined
                                                                style={{
                                                                    color: runwayColors.lightBackgroundGrey,
                                                                    marginRight: 4,
                                                                }}
                                                            />{' '}
                                                            ON
                                                        </div>
                                                    :   <div style={{ color: runwayColors.statusGrey }}>
                                                            {' '}
                                                            <CloseCircleOutlined style={{ marginRight: 4 }} /> OFF{' '}
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                            <span
                                                style={{
                                                    marginLeft: 24,
                                                    marginRight: 72,
                                                    marginBottom: 8,
                                                    color: runwayColors.statusGrey,
                                                }}
                                            >
                                                {item.description}
                                            </span>
                                        </div>,
                                )}
                            </div>
                        }
                    >
                        <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                    </Tooltip>
                </div>
            );
        }

        const ticketListData = value as TimelineEventTicketListData;
        if (ticketListData?.type === TimelineEventRowValueType.TicketList) {
            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 4,
                        textAlign: 'left',
                    }}
                >
                    <span>{ticketListData.text}</span>
                    <ul style={{ margin: 0, padding: 0 }}>
                        {ticketListData.tickets.map((ticketKey) => (
                            <li key={ticketKey}>{ticketKey}</li>
                        ))}
                    </ul>
                </div>
            );
        }

        const lastAutomationRun = value as TimelineEventLastAutomationRunData;
        if (lastAutomationRun?.status && lastAutomationRun?.executedAt) {
            const automationStatus = lastAutomationRun.status;

            return (
                <Space
                    direction="horizontal"
                    size={0}
                    style={{ width: '100%' }}
                    split={
                        <Divider
                            type="vertical"
                            style={{
                                backgroundColor: antGrey.grey5,
                            }}
                        />
                    }
                >
                    <div>
                        <Text style={{ color: antGrey.grey8 }}>{displayTextForAutomationStatus(automationStatus)}</Text>
                        {lastAutomationRun.errorMessage ?
                            <Tooltip overlayStyle={{ maxWidth: '500px' }} title={lastAutomationRun.errorMessage}>
                                <span style={{ marginLeft: 8 }}>
                                    {automationSettingsIconForStatus(automationStatus)}
                                </span>
                            </Tooltip>
                        :   <span style={{ marginLeft: 8 }}>{automationSettingsIconForStatus(automationStatus)}</span>}
                    </div>
                    <div>
                        <Tooltip
                            overlayStyle={{ maxWidth: '500px' }}
                            title={formatDateWithTimezone(
                                lastAutomationRun.executedAt,
                                'fullWithTimezone',
                                effectiveTimezone,
                                use24HourTimeFormat,
                            )}
                        >
                            <Text style={{ color: antGrey.grey8 }}>
                                {
                                    formatEventDate(
                                        lastAutomationRun.executedAt,
                                        TimelineEventStatus.Upcoming,
                                        null,
                                        true,
                                    ).label
                                }
                            </Text>
                        </Tooltip>
                    </div>
                </Space>
            );
        }

        if (typeof value === 'object' && value?.length) {
            const stepConfigNames = [];
            for (const item of value) {
                const stepConfig = stepGraphStepsMap[item];
                if (stepConfig?.stepName) {
                    stepConfigNames.push(stepConfig.stepName);
                }
            }

            if (stepConfigNames.length) {
                return <div>{stepConfigNames.join(', ')}</div>;
            }
        }

        const regressionTestingStrategies = getAutoSelectLatestBuildStrategyFieldData(appStoreType);
        const strategy = value as AutoSelectLatestBuildStrategy;
        if (strategy && regressionTestingStrategies?.[strategy]) {
            return <div>{regressionTestingStrategies[strategy].displayValue}</div>;
        }

        return (
            <div>
                {typeof value === 'object' ?
                    value && value.text ?
                        value.text
                    :   JSON.stringify(value)
                :   String(value)}
            </div>
        );
    }

    const formatEventDate = (
        eventDate: string,
        eventStatus: TimelineEventStatus,
        releaseStepType?: ReleaseStepType,
        isPastAutomationRun?: boolean,
    ): { label: string; isRelative: boolean } => {
        if (!eventDate) {
            return { label: '', isRelative: false };
        }

        const eventMoment = dayjs(eventDate);

        const now = dayjs();
        const diffMs = eventMoment.diff(now);

        const getFormattedDate = (formatType: TimeFormat) =>
            formatDateWithTimezone(eventMoment, formatType, effectiveTimezone, use24HourTimeFormat);

        if (
            releaseStepType ||
            eventStatus === TimelineEventStatus.Target ||
            timelineEvent.type === TimelineEventType.ChecklistItemDueUpcoming
        ) {
            return { label: getFormattedDate('shortMonthDateWithTime'), isRelative: false };
        }

        if (context === ReleaseTimelineSectionContext.AppHome) {
            return { label: getFormattedDate('short'), isRelative: false };
        }

        if (diffMs < 0) {
            const diffSecs = Math.abs(diffMs) / 1000;
            const diffMins = Math.floor(diffSecs / 60);
            const diffHours = Math.floor(diffMins / 60);

            if (eventStatus !== TimelineEventStatus.Upcoming) {
                if (diffHours < 24) {
                    if (diffHours < 1) {
                        return { label: `${diffMins}m ago`, isRelative: true };
                    } else if (diffHours === 1) {
                        return { label: `${diffHours} hour ago`, isRelative: true };
                    } else {
                        return { label: `${diffHours} hours ago`, isRelative: true };
                    }
                } else {
                    return { label: getFormattedDate('shortHourOnlyWithoutTimezone'), isRelative: false };
                }
            } else {
                if (!isPastAutomationRun) {
                    return { label: '', isRelative: false };
                }

                if (diffHours < 1) {
                    return { label: `${diffMins}m ago`, isRelative: true };
                } else if (diffHours === 1) {
                    return { label: `${diffHours} hour ago`, isRelative: true };
                } else if (diffHours < 24) {
                    return { label: `${diffHours} hours ago`, isRelative: true };
                } else if (diffHours < 336) {
                    return { label: `${Math.floor(diffHours / 24)}d ago`, isRelative: true };
                } else {
                    return { label: getFormattedDate('shortHourOnlyWithoutTimezone'), isRelative: false };
                }
            }
        } else {
            const diffSecs = diffMs / 1000;
            const diffMins = Math.floor(diffSecs / 60);
            const diffHours = Math.floor(diffMins / 60);

            if (diffHours < 1) {
                return { label: `in ${diffMins}m`, isRelative: true };
            } else if (diffHours === 1) {
                return { label: `in ${diffHours} hour`, isRelative: true };
            } else if (diffHours < 24) {
                return { label: `in ${diffHours} hours`, isRelative: true };
            } else if (diffHours < 48) {
                return { label: `in ${Math.floor(diffHours / 24)} day`, isRelative: true };
            } else {
                return { label: `in ${Math.floor(diffHours / 24)} days`, isRelative: true };
            }
        }
    };

    const triggeredAt =
        timelineEvent.status === TimelineEventStatus.Recurring ? timelineEvent.nextRetryAt : timelineEvent.triggeredAt;
    const { label: triggeredAtLabel, isRelative: isTriggeredAtRelativeTime } = formatEventDate(
        triggeredAt,
        timelineEvent.status,
        releaseStepType,
    );
    const triggeredByMoment = dayjs(triggeredAt);

    const titleColor = timelineEvent.status === TimelineEventStatus.Target ? runwayColors.primary : antGrey.grey9;
    const dateLabelColor = timelineEvent.status === TimelineEventStatus.Target ? runwayColors.primary : antGrey.grey7;
    const dateLabelStyle = {
        fontSize: 12,
        color: dateLabelColor,
        fontWeight: 400,
        whiteSpace: 'nowrap',
        display: 'block',
    };

    const title = titleForEvent({
        timelineEvent,
        appPlatform,
        associatedUser,
        appStoreType,
        isWorkflowsCIStepEnabled,
        use24HourTimeFormat,
        effectiveTimezone,
    });

    const titleContent = titleContentForEvent(timelineEvent, title);

    const automationData = timelineEvent.automationData;
    const additionalEventData = Object.entries(timelineEvent.additionalData ?? {});
    const lastAutomationRun = Object.entries(timelineEvent.lastAutomationRun ?? {});
    const isAutomationPending = automationData?.automationStatus === AutomationStatus.Pending;
    const isAutomationRecurring = timelineEvent.status === TimelineEventStatus.Recurring;

    let automationStepRunDisplayName = '';
    let automationStatusDisplayText = '';
    let showStrongAutomationStatusText = true;
    const isAutomationDataFailureACodeBlock =
        automationData?.failureReason?.startsWith('```') && automationData?.failureReason?.endsWith('```');
    if (automationData) {
        if (automationData.stepRunDisplayName) {
            automationStepRunDisplayName = automationData.stepRunDisplayName;
        } else if (timelineEvent.releaseStepArchetypes?.length > 0) {
            automationStepRunDisplayName = displayTextForReleaseStepType(
                timelineEvent.releaseStepArchetypes[0],
                appPlatform,
                appStoreType,
                isWorkflowsCIStepEnabled,
            );
        }

        automationStatusDisplayText = displayTextForAutomationStatus(
            automationData.automationStatus,
            isAutomationRetrying,
            isAutomationRecurring,
        );
        if (!isCollapseExpanded && automationData.failureReason && !isAutomationDataFailureACodeBlock) {
            automationStatusDisplayText = automationData.failureReason;
            showStrongAutomationStatusText = false;
        }
    }

    const eventDestinations = React.useMemo(() => {
        return (
            timelineEvent.destinationStepConfigIds?.reduce((acc: Destination[], stepConfigId) => {
                const destination = destinationsMap[stepConfigId];

                if (destination) {
                    acc.push(destination);
                }

                return acc;
            }, []) ?? []
        );
    }, [timelineEvent.destinationStepConfigIds, destinationsMap]);

    React.useEffect(() => {
        if (success) {
            globalMessage.success({
                content: 'Automation retry was triggered!',
                key: 'retryAutomationRun',
            });
        }
    }, [success]);

    React.useEffect(() => {
        if (retryAutomationError) {
            globalMessage.error(retryAutomationError);
        }
    }, [retryAutomationError]);

    return (
        <>
            <TimelineEventItemBase
                isUsedInStepSummary={isUsedInStepSummary}
                isCompact={isCompact}
                isTargetEvent={timelineEvent.status === TimelineEventStatus.Target}
                attributionIcon={
                    timelineEvent.type === TimelineEventType.ChecklistItemDueUpcoming ?
                        <ChecklistItemOwnerAttribution
                            eventData={timelineEvent.eventData as TimelineEventChecklistItemDueUpcomingData}
                            orgId={orgId}
                            appId={appId}
                            releaseId={timelineEvent.releaseId ?? ''}
                        />
                    : timelineEvent.status === TimelineEventStatus.Target ?
                        attributionIconForTargetEvent({
                            timelineEvent,
                        })
                    :   attributionIconForEvent({
                            timelineEvent,
                            appPlatform,
                            user,
                        })

                }
                nodeIcon={
                    <div
                        style={{
                            height: 10,
                            width: 10,
                            borderRadius: '50%',
                            marginTop: 3,
                            border:
                                '2px solid ' +
                                getTimelineColorForEvent({ timelineEvent, isRetrying: isAutomationRetrying }),
                        }}
                    ></div>
                }
                eventIcon={
                    <Flex align="center" style={{ marginLeft: 15, height: 15 }} gap={8}>
                        <Icon
                            component={getTimelineIconForEvent({ timelineEvent })}
                            style={{
                                display: 'block',
                                fontSize: 15,
                                color: antGrey.grey7,
                                marginTop: 1,
                            }}
                        />
                        {(
                            !shouldHideEventCount &&
                            timelineEvent.automationData?.automationStatus === AutomationStatus.Failed &&
                            timelineEvent.previouslySeenCount
                        ) ?
                            <TimelineEventCounter previouslySeenCount={timelineEvent.previouslySeenCount} />
                        :   null}
                    </Flex>
                }
                primaryLabel={
                    timelineEvent.status === TimelineEventStatus.Target ?
                        <Tag color={colorWithAlpha(rwColors.primary, 0.15)} style={{ ...tagStyle, color: titleColor }}>
                            {title}
                        </Tag>
                    :   <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                minWidth: 0,
                                minHeight: '19px',
                            }}
                        >
                            {(
                                (context === ReleaseTimelineSectionContext.AppTimeline ||
                                    context === ReleaseTimelineSectionContext.OrgTimeline) &&
                                (timelineEvent.releaseData?.displayableVersion || appName)
                            ) ?
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        flexShrink: 0,
                                        boxShadow: `inset 0 0 0 1px ${antGrey.grey4}`,
                                        borderRadius: 4,
                                        padding: '3px 6px',
                                        backgroundColor: antGrey.grey2,
                                    }}
                                >
                                    {eventDestinations.length > 0 && (
                                        <div style={{ lineHeight: 0, flexShrink: 0 }}>
                                            <DestinationCounter
                                                size={15}
                                                destinations={eventDestinations}
                                                platformLabelOverride={mdmOverride?.label}
                                                platformIconOverride={mdmOverride?.icon}
                                            />
                                        </div>
                                    )}
                                    {appName && (
                                        <Typography.Text
                                            style={{
                                                fontWeight: 500,
                                                color: titleColor,
                                                whiteSpace: 'nowrap',
                                                lineHeight: '15px',
                                                maxWidth: 120,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                            title={appName}
                                        >
                                            {appName}
                                        </Typography.Text>
                                    )}
                                    {timelineEvent.releaseData?.displayableVersion && (
                                        <Typography.Text
                                            style={{
                                                fontWeight: 500,
                                                color: titleColor,
                                                whiteSpace: 'nowrap',
                                                lineHeight: '15px',
                                            }}
                                        >
                                            {timelineEvent.releaseData.displayableVersion}
                                        </Typography.Text>
                                    )}
                                </span>
                            :   eventDestinations.length > 0 && (
                                    <div style={{ flexShrink: 0 }}>
                                        <DestinationCounter
                                            size={15}
                                            destinations={eventDestinations}
                                            platformLabelOverride={mdmOverride?.label}
                                            platformIconOverride={mdmOverride?.icon}
                                        />
                                    </div>
                                )
                            }
                            <Typography.Text
                                style={{
                                    color: titleColor,
                                    fontWeight: 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    minWidth: 0,
                                    lineHeight: '19px',
                                }}
                                title={title}
                            >
                                {titleContent}
                            </Typography.Text>
                        </div>
                }
                secondaryLabel={
                    (
                        timelineEvent.status === TimelineEventStatus.Recurring ||
                        timelineEvent.status === TimelineEventStatus.Upcoming
                    ) ?
                        triggeredAtLabel ?
                            <Text style={dateLabelStyle}>{triggeredAtLabel}</Text>
                        : timelineEvent.status === TimelineEventStatus.Recurring ?
                            <Text style={dateLabelStyle}>Active</Text>
                        :   null
                    :   <TimeWithConditionalTz
                            triggeredAtLabel={triggeredAtLabel}
                            triggeredByMoment={triggeredByMoment}
                            use24HourTimeFormat={use24HourTimeFormat}
                            effectiveTimezone={effectiveTimezone}
                            style={dateLabelStyle}
                            isRelativeTime={isTriggeredAtRelativeTime}
                        />
                }
                automationDataTable={
                    !releaseStepType && context !== ReleaseTimelineSectionContext.AppHome && automationData ?
                        <div style={{ marginBottom: 30, maxWidth: 720 }}>
                            <Collapse
                                onChange={handleCollapseChange}
                                collapsible={additionalEventData ? 'header' : 'disabled'}
                                defaultActiveKey={[]}
                                items={[
                                    {
                                        key: '1',
                                        label: !!automationData.stepRunDisplayName && (
                                            <>
                                                {' '}
                                                <Text
                                                    style={{
                                                        fontSize: '12px',
                                                        color: antGrey.grey7,
                                                        letterSpacing: '0.1em',
                                                        textTransform: 'uppercase',
                                                        marginRight: 8,
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Step
                                                </Text>
                                                <Text type="secondary">{automationStepRunDisplayName}</Text>
                                                {!isCollapseExpanded &&
                                                    automationData.automationStatus === AutomationStatus.Failed &&
                                                    isAutomationDataFailureACodeBlock && (
                                                        <Text type="secondary" italic style={{ marginLeft: 12 }}>
                                                            Expand for cherry-pick instructions
                                                        </Text>
                                                    )}
                                            </>
                                        ),
                                        children: additionalEventData && (
                                            <div>
                                                {automationData.failureReason &&
                                                    !isAutomationRetrying &&
                                                    (isAutomationDataFailureACodeBlock ?
                                                        <Tooltip title="Click to copy">
                                                            <pre
                                                                onClick={() =>
                                                                    copyToClipboard(
                                                                        automationData.failureReason
                                                                            .slice(3, -3)
                                                                            .trim(),
                                                                    )
                                                                }
                                                                style={{
                                                                    backgroundColor: antGrey.grey2,
                                                                    border: `1px solid ${antGrey.grey4}`,
                                                                    borderRadius: 6,
                                                                    padding: '8px 12px',
                                                                    marginLeft: 200,
                                                                    color: runwayColors.statusRed,
                                                                    fontFamily: 'monospace',
                                                                    fontSize: 12,
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                    cursor: 'pointer',
                                                                    textAlign: 'left',
                                                                    position: 'relative',
                                                                }}
                                                            >
                                                                <CopyOutlined
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: 8,
                                                                        right: 8,
                                                                        color: antGrey.grey6,
                                                                    }}
                                                                />
                                                                {automationData.failureReason.slice(3, -3).trim()}
                                                            </pre>
                                                        </Tooltip>
                                                    :   <Typography.Paragraph
                                                            ellipsis={{
                                                                rows: 3,
                                                                tooltip: automationData.failureReason,
                                                            }}
                                                            style={{
                                                                color: runwayColors.statusRed,
                                                                textAlign: 'right',
                                                                marginLeft: 200,
                                                            }}
                                                        >
                                                            {automationData.failureReason}
                                                        </Typography.Paragraph>)}
                                                {additionalEventData?.length ?
                                                    <>
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'start',
                                                                marginBottom: 8,
                                                            }}
                                                        >
                                                            <Space size={0}>
                                                                <Text
                                                                    style={{
                                                                        fontSize: '12px',
                                                                        color: antGrey.grey7,
                                                                        letterSpacing: '0.1em',
                                                                        textTransform: 'uppercase',
                                                                        marginRight: 8,
                                                                        fontWeight: 400,
                                                                        marginLeft: 5,
                                                                    }}
                                                                >
                                                                    {(
                                                                        timelineEvent.status ===
                                                                        TimelineEventStatus.Upcoming
                                                                    ) ?
                                                                        'Details '
                                                                    :   'Runtime Details'}
                                                                </Text>
                                                            </Space>
                                                        </div>
                                                        <div
                                                            style={{
                                                                backgroundColor: antGrey.grey2,
                                                                borderRadius: '8px',
                                                                border: `1px solid ${antGrey.grey5}`,
                                                            }}
                                                        >
                                                            <Space
                                                                direction="vertical"
                                                                size={0}
                                                                style={{ width: '100%' }}
                                                                split={
                                                                    <Divider
                                                                        style={{
                                                                            backgroundColor: antGrey.grey5,
                                                                            margin: 0,
                                                                            width: '100%',
                                                                        }}
                                                                    />
                                                                }
                                                            >
                                                                {additionalEventData?.map(([key, value]) => {
                                                                    return (
                                                                            key ===
                                                                                AutomationTimelineEventRowKey.RelatedAutomations
                                                                        ) ?
                                                                            null
                                                                        :   <div
                                                                                key={key}
                                                                                style={{
                                                                                    display: 'flex',
                                                                                    justifyContent: 'space-between',
                                                                                    alignItems: 'start',
                                                                                    padding: '6px 10px',
                                                                                }}
                                                                            >
                                                                                <Space size={0}>
                                                                                    <Text
                                                                                        type="secondary"
                                                                                        style={{
                                                                                            color: antGrey.grey7,
                                                                                            whiteSpace: 'nowrap',
                                                                                            overflow: 'hidden',
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            textOverflow: 'ellipsis',
                                                                                        }}
                                                                                    >
                                                                                        {key}
                                                                                    </Text>
                                                                                </Space>
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column',
                                                                                        alignItems: 'end',
                                                                                        gap: 4,
                                                                                        marginLeft: 16,
                                                                                    }}
                                                                                >
                                                                                    <Space size={0}>
                                                                                        <Text
                                                                                            type="secondary"
                                                                                            style={{
                                                                                                color: antGrey.grey8,
                                                                                                textAlign: 'end',
                                                                                            }}
                                                                                        >
                                                                                            {renderTimelineValue(value)}
                                                                                        </Text>
                                                                                    </Space>
                                                                                </div>
                                                                            </div>;
                                                                })}
                                                            </Space>
                                                        </div>
                                                    </>
                                                :   null}
                                                <div
                                                    key={'settings'}
                                                    style={{
                                                        alignItems: 'right',
                                                        marginTop: 10,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'end',
                                                            gap: 4,
                                                        }}
                                                    >
                                                        <Space
                                                            direction="horizontal"
                                                            size={1}
                                                            style={{ height: '100%' }}
                                                            split={
                                                                <Divider
                                                                    style={{
                                                                        backgroundColor: antGrey.grey5,
                                                                        margin: 0,
                                                                        height: 20,
                                                                        width: 1,
                                                                    }}
                                                                />
                                                            }
                                                        >
                                                            <Text
                                                                type="secondary"
                                                                onClick={handleSettingsModalOpenedChange}
                                                                style={{
                                                                    paddingRight: shouldDisplayRetryButton ? 10 : 0,
                                                                    color: runwayColors.darkTextGrey,
                                                                    textDecorationLine: 'underline',
                                                                    textDecorationStyle: 'dotted',
                                                                    textUnderlineOffset: 3,
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                View/edit current automation settings
                                                            </Text>
                                                            {shouldDisplayRetryButton ?
                                                                isAutomationRetrying ?
                                                                    <Tooltip
                                                                        title="Automation retry triggered"
                                                                        placement="top"
                                                                    >
                                                                        <Button
                                                                            icon={<ReloadOutlined />}
                                                                            type="link"
                                                                            style={{ paddingRight: 0, paddingLeft: 10 }}
                                                                            disabled
                                                                        >
                                                                            Retry automation
                                                                        </Button>
                                                                    </Tooltip>
                                                                :   <Button
                                                                        icon={<ReloadOutlined />}
                                                                        type="link"
                                                                        style={{ paddingRight: 0, paddingLeft: 10 }}
                                                                        disabled={isAutomationRetrying}
                                                                        onClick={() =>
                                                                            retryAutomation({
                                                                                orgId: timelineEvent.orgId,
                                                                                appId: timelineEvent.appId,
                                                                                releaseId: timelineEvent.releaseId,
                                                                                workflowId,
                                                                                workflowRunId:
                                                                                    timelineEvent.workflowRunId,
                                                                                stepRunId,
                                                                                automationRunId: automationRun?.id,
                                                                                automationType:
                                                                                    timelineEvent.automationData
                                                                                        ?.automationType,
                                                                                eventData: timelineEvent.eventData,
                                                                                eventType: timelineEvent.type,
                                                                            })
                                                                        }
                                                                    >
                                                                        Retry automation
                                                                    </Button>

                                                            :   null}
                                                        </Space>
                                                    </div>
                                                </div>
                                                {isSettingsModalOpened && (
                                                    <AppAutomationSettings
                                                        onAutomationSelected={() => {}}
                                                        appId={timelineEvent.appId}
                                                        orgId={timelineEvent.orgId}
                                                        onClose={() => setIsSettingsModalOpened(false)}
                                                        open={isSettingsModalOpened}
                                                        automationId={automationData.automationId}
                                                        onAfterClose={() => {}}
                                                    />
                                                )}
                                                {timelineEvent.previouslySeenWithErrorAt?.length ?
                                                    <>
                                                        <Space size={0}>
                                                            <Text
                                                                style={{
                                                                    fontSize: '12px',
                                                                    color: antGrey.grey7,
                                                                    letterSpacing: '0.1em',
                                                                    textTransform: 'uppercase',
                                                                    marginRight: 8,
                                                                    fontWeight: 400,
                                                                    marginLeft: 5,
                                                                }}
                                                            >
                                                                History
                                                            </Text>
                                                        </Space>
                                                        <div
                                                            style={{
                                                                borderRadius: '8px',
                                                                border: `1px solid ${antGrey.grey5}`,
                                                            }}
                                                        >
                                                            <Space
                                                                direction="vertical"
                                                                size={0}
                                                                style={{ width: '100%' }}
                                                                split={
                                                                    <Divider
                                                                        style={{
                                                                            backgroundColor: antGrey.grey5,
                                                                            margin: 0,
                                                                            width: '100%',
                                                                        }}
                                                                    />
                                                                }
                                                            >
                                                                {timelineEvent.previouslySeenWithErrorAt.map(
                                                                    (previousEvent, index) => (
                                                                        <div
                                                                            key={`${timelineEvent.type}-${timelineEvent.triggeredAt}-${index}`}
                                                                            style={{
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                alignItems: 'start',
                                                                                padding: '6px 10px',
                                                                            }}
                                                                        >
                                                                            <Space size={0}>
                                                                                <Tooltip
                                                                                    overlayStyle={{ maxWidth: '500px' }}
                                                                                    title={formatDateWithTimezone(
                                                                                        previousEvent.triggeredAt,
                                                                                        'fullWithTimezone',
                                                                                        effectiveTimezone,
                                                                                        use24HourTimeFormat,
                                                                                    )}
                                                                                >
                                                                                    <Text
                                                                                        type="secondary"
                                                                                        style={{
                                                                                            color: antGrey.grey8,
                                                                                            whiteSpace: 'nowrap',
                                                                                            overflow: 'hidden',
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            textOverflow: 'ellipsis',
                                                                                        }}
                                                                                    >
                                                                                        {formatDateWithTimezone(
                                                                                            previousEvent.triggeredAt,
                                                                                            'shortHourOnlyWithoutTimezone',
                                                                                            effectiveTimezone,
                                                                                            use24HourTimeFormat,
                                                                                        )}
                                                                                    </Text>
                                                                                </Tooltip>
                                                                            </Space>
                                                                            <div
                                                                                style={{
                                                                                    display: 'flex',
                                                                                    flexDirection: 'column',
                                                                                    alignItems: 'end',
                                                                                    gap: 4,
                                                                                    marginLeft: 16,
                                                                                }}
                                                                            >
                                                                                <Space size={0}>
                                                                                    <Text
                                                                                        type="secondary"
                                                                                        ellipsis={{ tooltip: true }}
                                                                                        style={{
                                                                                            color: antGrey.grey8,
                                                                                            whiteSpace: 'nowrap',
                                                                                            overflow: 'hidden',
                                                                                            maxWidth: 400,
                                                                                        }}
                                                                                    >
                                                                                        {previousEvent.previousError}
                                                                                    </Text>
                                                                                    <span
                                                                                        style={{
                                                                                            marginLeft: 8,
                                                                                        }}
                                                                                    >
                                                                                        {automationSettingsIconForStatus(
                                                                                            AutomationStatus.Failed,
                                                                                        )}
                                                                                    </span>
                                                                                </Space>
                                                                            </div>
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </Space>
                                                        </div>
                                                    </>
                                                :   null}
                                                {lastAutomationRun?.length ?
                                                    <>
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'start',
                                                                marginBottom: 8,
                                                            }}
                                                        >
                                                            <Space size={0}>
                                                                <Text
                                                                    style={{
                                                                        fontSize: '12px',
                                                                        color: antGrey.grey7,
                                                                        letterSpacing: '0.1em',
                                                                        textTransform: 'uppercase',
                                                                        marginRight: 8,
                                                                        fontWeight: 400,
                                                                        marginLeft: 5,
                                                                    }}
                                                                >
                                                                    Last Run Details (Previous Release)
                                                                </Text>
                                                            </Space>
                                                        </div>
                                                        <div
                                                            style={{
                                                                borderRadius: '8px',
                                                                border: `1px solid ${antGrey.grey5}`,
                                                            }}
                                                        >
                                                            <Space
                                                                direction="vertical"
                                                                size={0}
                                                                style={{ width: '100%' }}
                                                                split={
                                                                    <Divider
                                                                        style={{
                                                                            backgroundColor: antGrey.grey5,
                                                                            margin: 0,
                                                                            width: '100%',
                                                                        }}
                                                                    />
                                                                }
                                                            >
                                                                {lastAutomationRun.map(([key, value]) => (
                                                                    <div
                                                                        key={key}
                                                                        style={{
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'start',
                                                                            padding: '6px 10px',
                                                                        }}
                                                                    >
                                                                        <Space size={0}>
                                                                            <Text
                                                                                type="secondary"
                                                                                style={{
                                                                                    color: antGrey.grey7,
                                                                                    whiteSpace: 'nowrap',
                                                                                    overflow: 'hidden',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    textOverflow: 'ellipsis',
                                                                                }}
                                                                            >
                                                                                {key}
                                                                            </Text>
                                                                        </Space>
                                                                        <div
                                                                            style={{
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                alignItems: 'end',
                                                                                gap: 4,
                                                                                marginLeft: 16,
                                                                            }}
                                                                        >
                                                                            <Space size={0}>
                                                                                <Text
                                                                                    type="secondary"
                                                                                    style={{
                                                                                        color: antGrey.grey8,
                                                                                    }}
                                                                                >
                                                                                    {renderTimelineValue(value)}
                                                                                </Text>
                                                                            </Space>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </Space>
                                                        </div>
                                                    </>
                                                :   null}
                                            </div>
                                        ),
                                        extra: (
                                            <div>
                                                <Text
                                                    style={{
                                                        color: colorForAutomationStatus(
                                                            automationData.automationStatus,
                                                            false,
                                                            isAutomationRetrying,
                                                            isAutomationPending,
                                                        ),
                                                        maxWidth: 370,
                                                    }}
                                                    ellipsis={{ tooltip: true }}
                                                    strong={showStrongAutomationStatusText}
                                                >
                                                    {renderTextWithMarkdownLinks(automationStatusDisplayText)}
                                                </Text>
                                                <span style={{ marginLeft: 8 }}>
                                                    {automationSettingsIconForStatus(
                                                        automationData.automationStatus,
                                                        isAutomationRetrying,
                                                        isAutomationPending,
                                                        isAutomationRecurring,
                                                    )}
                                                </span>
                                            </div>
                                        ),
                                    },
                                ]}
                                bordered
                                style={{ background: runwayColors.white }}
                            />
                        </div>
                    :   null
                }
                additionalDataTableForNonAutomations={
                    (
                        !releaseStepType &&
                        context !== ReleaseTimelineSectionContext.AppHome &&
                        !automationData &&
                        additionalEventData?.length > 0
                    ) ?
                        <div
                            style={{
                                marginBottom: 30,
                                maxWidth: 720,
                                backgroundColor: antGrey.grey2,
                                padding: '12px',
                                borderRadius: '8px',
                                border: `1px solid ${antGrey.grey5}`,
                            }}
                        >
                            <Space
                                direction="vertical"
                                size={12}
                                style={{ width: '100%' }}
                                split={<Divider style={{ backgroundColor: antGrey.grey5, margin: 0 }} />}
                            >
                                {additionalEventData.map(([key, value]) => {
                                    return (
                                        <div
                                            key={key}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'start',
                                            }}
                                        >
                                            <Text type="secondary" style={{ color: antGrey.grey7 }}>
                                                {key}
                                            </Text>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'end',
                                                    gap: 4,
                                                }}
                                            >
                                                <Text type="secondary" style={{ color: antGrey.grey8 }}>
                                                    {renderTimelineValue(value)}
                                                </Text>
                                            </div>
                                        </div>
                                    );
                                })}
                            </Space>
                        </div>
                    : (
                        timelineEvent.type === TimelineEventType.ReleaseStepIntegrationSettingsUpdated &&
                        !releaseStepType &&
                        context !== ReleaseTimelineSectionContext.AppHome
                    ) ?
                        <IntegrationSettingsUpdateCollapse
                            settingsData={timelineEvent.eventData as TimelineEventIntegrationUpdatedData}
                            associatedUser={associatedUser}
                            orgId={orgId}
                            appId={appId}
                            isStepLevel={true}
                            handleCollapseChange={handleCollapseChange}
                        />
                    : timelineEvent.type === TimelineEventType.IntegrationUpdated ?
                        <IntegrationSettingsUpdateCollapse
                            settingsData={timelineEvent.eventData as TimelineEventIntegrationUpdatedData}
                            associatedUser={associatedUser}
                            orgId={orgId}
                            appId={appId}
                            isStepLevel={false}
                            handleCollapseChange={handleCollapseChange}
                        />
                    : timelineEvent.type === TimelineEventType.BatchActionRequested ?
                        <BatchActionRequestDetailsCollapse
                            data={timelineEvent.eventData as TimelineEventBatchActionRequestedData}
                            associatedUser={associatedUser}
                            appId={appId}
                            handleCollapseChange={handleCollapseChange}
                            effectiveTimezone={effectiveTimezone}
                            use24HourTimeFormat={use24HourTimeFormat}
                        />
                    :   null
                }
            />
        </>
    );
};

const getAssociatedUser = (timelineEvent: TimelineEvent) => {
    let associatedUserId = null;

    if (timelineEvent.type === TimelineEventType.ReleasePilotChanged) {
        associatedUserId = (timelineEvent.eventData as TimelineEventReleasePilotChangedData).releasePilotId;
    }

    if (
        timelineEvent.type === TimelineEventType.ReleaseStepIntegrationSettingsUpdated ||
        timelineEvent.type === TimelineEventType.IntegrationUpdated
    ) {
        associatedUserId = (timelineEvent.eventData as TimelineEventIntegrationUpdatedData).changedByUser;
    }

    if (timelineEvent.type === TimelineEventType.BatchActionRequested && !!timelineEvent.userId) {
        associatedUserId = timelineEvent.userId;
    }

    return associatedUserId;
};
